import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import BuildIcon from "@mui/icons-material/Build";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import HistoryIcon from "@mui/icons-material/History";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  downloadArtifactFile,
  executeCasperWorkflow,
  executeCasperEntity,
  auditCasperPreviewEvent,
  createKey,
  getCasperOnboardingStatus,
  getCasperProfile,
  getCasperSession,
  getCasperWorkflowRunStatus,
  getAgentRunArtifacts,
  listCasperSessions,
  planCasperIntent,
  respondToApproval,
  registerUser,
  runCasperChat,
  saveCasperWorkflow,
  startOAuthConnect,
  streamAgentRunResult,
  submitCasperFeedback,
  uploadCasperFiles,
  uploadCasperSessionFiles,
} from "../../api/agentApi";
import { useUser } from "../UserContext";
import { PERMISSIONS } from "../../permissions";
import CasperExecutionPreviewCard from "./CasperExecutionPreviewCard.jsx";
import CasperRunCard from "./CasperRunCard";
import CasperInteractionDecisionCard from "./CasperInteractionDecisionCard.jsx";
import {
  shouldShowExecutionPreviewCard,
  shouldShowInteractionDecisionCard,
} from "./casperInteractionDecisionDisplay";
import CasperMarkdown from "./CasperMarkdown.jsx";
import {
  hasRestorableCasperLocalTranscript,
  messageHasWorkflowContext,
  shouldPersistCasperLocalTranscript,
} from "./casperTranscriptPersistence";
import { buildCasperHistoryItems } from "./casperHistory";
import {
  CASPER_ONBOARDING_SKIPPED_PREFIX,
  getReviewableOnboardingTasks,
  getAllPendingOnboardingTasks,
  getPendingOnboardingTasks,
  interpretOnboardingKeyReply,
  isCasperSetupPrompt,
  onboardingCredentialOriginLabel,
  onboardingTaskStorageId,
} from "./onboardingUtils";
import {
  rankedRoutePickerEntities,
  resolutionFromRouteAlternatives,
  routePickerTitle,
  shouldShowRoutePicker,
} from "./casperRoutePicker";
import { buildPreviewEntityExecutionRequest } from "./casperExecutionDispatch";
import { runContextsFromSessionData } from "./casperArtifactRuns";
import { buildRepreviewPlanArgs } from "./casperRepreview";
import { isCasperControlKeyword } from "../../constants/casperControlKeywords";

const copper = "#B87333";
const copperDark = "#935C29";
const casperAppBootId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const EMPTY_ENTITIES = Object.freeze([]);
const QUICK_PROMPTS = [
  "Summarize these files",
  "Create a campaign brief",
  "Find the right agent",
  "Draft a client-ready response",
];
const CASPER_ACTIVE_SESSION_PREFIX = "nextmoca:casper:active-session";
const CASPER_LOCAL_TRANSCRIPT_PREFIX = "nextmoca:casper:local-transcript";
const CASPER_ONBOARDING_REVIEWED_PREFIX = "nextmoca:casper:onboarding-reviewed";
const CASPER_ONBOARDING_COMPLETE_PREFIX = "nextmoca:casper:onboarding-complete";

function onboardingReadinessFingerprint(status) {
  if (!status || !Array.isArray(status.providers)) return "";
  const providers = status.providers
    .map((provider) => {
      const envs = Array.isArray(provider.required_envs)
        ? provider.required_envs.join(",")
        : provider.required_env || "";
      return [
        provider.provider || provider.provider_label || "provider",
        provider.configured ? "ready" : "missing",
        envs,
      ].join(":");
    })
    .sort()
    .join("|");
  return [
    status.ready ? "ready" : "needs_setup",
    status.completion_percent || 0,
    providers,
  ].join("::");
}

const commandIconSx = {
  width: 46,
  height: 46,
  border: "1px solid rgba(184,115,51,0.28)",
  color: copperDark,
  bgcolor: "rgba(246,240,235,0.72)",
  boxShadow: "0 10px 24px rgba(20,18,15,0.06)",
  transition: "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
  "&:hover": {
    bgcolor: "rgba(184,115,51,0.12)",
    transform: "translateY(-1px)",
    boxShadow: "0 14px 30px rgba(20,18,15,0.10)",
  },
  "&.Mui-disabled": {
    opacity: 0.45,
    color: "text.disabled",
  },
};

const messageActionButtonSx = (active = false) => ({
  width: 30,
  height: 30,
  borderRadius: "50%",
  border: `1px solid ${active ? "#B87333" : "rgba(184,115,51,0.24)"}`,
  color: active ? "#935C29" : "#8a98aa",
  bgcolor: active ? "rgba(184,115,51,0.11)" : "rgba(255,255,255,0.78)",
  boxShadow: active ? "0 7px 18px rgba(184,115,51,0.18)" : "none",
  transition: "all 0.16s ease",
  "& .MuiSvgIcon-root": {
    fontSize: 16,
  },
  "&:hover": {
    borderColor: "#B87333",
    color: "#935C29",
    bgcolor: "rgba(184,115,51,0.12)",
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    borderColor: "rgba(138,152,170,0.14)",
    color: "rgba(138,152,170,0.34)",
    bgcolor: "rgba(255,255,255,0.45)",
    transform: "none",
  },
});

function CasperGhost({ size = 56 }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        position: "relative",
        filter: "drop-shadow(0 12px 24px rgba(184, 115, 51, 0.22))",
        animation: "casperFloat 2.8s ease-in-out infinite",
        "@keyframes casperFloat": {
          "0%, 100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(-7px)" },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "48% 48% 42% 42%",
          background: "linear-gradient(145deg, #D1A17A 0%, #B87333 56%, #8E5727 100%)",
          "&:before": {
            content: '""',
            position: "absolute",
            width: size * 0.12,
            height: size * 0.12,
            borderRadius: "50%",
            background: "#10100F",
            top: size * 0.34,
            left: size * 0.3,
            boxShadow: `${size * 0.28}px 0 0 #10100F`,
          },
          "&:after": {
            content: '""',
            position: "absolute",
            right: -size * 0.16,
            bottom: size * 0.05,
            width: size * 0.45,
            height: size * 0.22,
            borderRadius: "999px",
            background: "linear-gradient(90deg, rgba(184,115,51,0.86), rgba(184,115,51,0))",
            transform: "rotate(-15deg)",
          },
        }}
      />
    </Box>
  );
}

function WorkingCasperIcon() {
  return (
    <Box
      aria-hidden
      sx={{
        width: 24,
        height: 24,
        position: "relative",
        animation: "casperDance 820ms ease-in-out infinite",
        "@keyframes casperDance": {
          "0%, 100%": { transform: "translateY(0) rotate(-2deg)" },
          "50%": { transform: "translateY(-3px) rotate(3deg)" },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: "2px",
          borderRadius: "48% 48% 42% 42%",
          background: "linear-gradient(145deg, #D1A17A 0%, #B87333 58%, #8E5727 100%)",
          "&:before": {
            content: '""',
            position: "absolute",
            width: 3,
            height: 3,
            borderRadius: "50%",
            background: "#10100F",
            top: 9,
            left: 7,
            boxShadow: "8px 0 0 #10100F",
          },
          "&:after": {
            content: '""',
            position: "absolute",
            right: -7,
            bottom: 2,
            width: 14,
            height: 7,
            borderRadius: "999px",
            background: "linear-gradient(90deg, rgba(184,115,51,0.9), rgba(184,115,51,0))",
            transform: "rotate(-14deg)",
          },
        }}
      />
    </Box>
  );
}

function makeLocalMessage(role, content, extra = {}) {
  return {
    id: extra.id || `local-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    role,
    content,
    status: extra.status || "completed",
    kind: extra.kind || "message",
    created_at: extra.created_at || new Date().toISOString(),
    ...extra,
  };
}

function isImageAttachment(attachment) {
  const mimeType = String(attachment?.type || attachment?.mime_type || "").toLowerCase();
  const name = String(attachment?.name || attachment?.filename || attachment?.relative_path || "").toLowerCase();
  return mimeType.startsWith("image/")
    || /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i.test(name);
}

function attachmentPromptReference(attachment) {
  return {
    id: attachment.id || attachment.relative_path || attachment.name,
    name: attachment.name || attachment.filename || attachment.relative_path || "uploaded file",
    filename: attachment.filename || attachment.name,
    relative_path: attachment.relative_path,
    mime_type: attachment.type || attachment.mime_type,
    size: attachment.size,
    kind: "session_upload",
  };
}

function mapServerMessage(message) {
  const metadata = message.metadata || {};
  const outcomeFeedback = metadata.outcome_feedback || {};
  return {
    id: message.id,
    role: message.role,
    content: message.content || "",
    status: message.status || "completed",
    kind: "message",
    run_number: message.run_number,
    agent_version: message.agent_version,
    created_at: message.created_at,
    attachments: metadata.attachments || metadata.files || [],
    metadata,
    feedback: outcomeFeedback.feedback || null,
    feedbackNote: outcomeFeedback.note || null,
    feedbackPending: false,
  };
}

function isUnfinishedAssistantMessage(message) {
  return message?.role === "assistant" && !["completed", "failed"].includes(message?.status || "completed");
}

function agentFromSession(session) {
  if (!session?.agent_id) return null;
  return {
    id: session.agent_id,
    version: session.agent_version,
    type: "agent",
    display_name: String(session.title || "").replace(/^Casper:\s*/i, "") || "Current agent",
    name: String(session.title || "").replace(/^Casper:\s*/i, "") || "Current agent",
  };
}

function readableEntityType(entity) {
  if (!entity?.type) return "capability";
  if (entity.type === "knowledge") return "knowledge source";
  return entity.type;
}

function entityRouteCopy(entity) {
  const name = entity?.display_name || entity?.name || "the best available match";
  return `I found ${name}. I’ll use this ${readableEntityType(entity)} here.`;
}

function formatSessionTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function entityIcon(type) {
  if (type === "workflow") return <AccountTreeIcon />;
  if (type === "tool") return <BuildIcon />;
  if (type === "knowledge") return <MenuBookIcon />;
  return <PsychologyIcon />;
}

function EntityCard({ entity, selected, onSelect, onRun }) {
  const typeLabel = entity.type === "knowledge" ? "Source" : entity.type?.[0]?.toUpperCase() + entity.type?.slice(1);
  const confidence = Math.round((entity.score || 0) * 100);
  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: 2,
        borderColor: selected ? copper : "divider",
        background: selected ? "rgba(184, 115, 51, 0.06)" : "rgba(255,255,255,0.82)",
        backdropFilter: "blur(16px)",
        transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 14px 34px rgba(20, 18, 15, 0.10)",
          borderColor: copper,
        },
      }}
    >
      <CardActionArea onClick={onSelect} sx={{ height: "100%", p: 2.25 }}>
        <Stack spacing={1.4}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                color: copperDark,
                bgcolor: "rgba(184, 115, 51, 0.11)",
              }}
            >
              {entityIcon(entity.type)}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={800} noWrap>
                {entity.display_name || entity.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {typeLabel}{confidence > 0 ? ` · ${confidence}% match` : ""}
              </Typography>
            </Box>
          </Stack>
          <Typography color="text.secondary" sx={{ minHeight: 48 }}>
            {(entity.description || entity.reason || "Available through Casper.").slice(0, 180)}
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Chip
              size="small"
              variant="outlined"
              label={entity.status === "auth_required" ? "Connect" : selected ? "Selected" : "Ready"}
              sx={{ borderColor: "rgba(184,115,51,0.42)", color: copperDark, fontWeight: 750 }}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onRun();
              }}
            >
              {entity.type === "tool" || entity.type === "knowledge" ? "Use" : "Start"}
            </Button>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

// Retained temporarily for older snapshots while onboarding moves fully into the setup drawer.
// eslint-disable-next-line no-unused-vars
function CasperOnboardingCard({
  status,
  loading,
  onRefresh,
  onDismiss,
  onStartGuide,
  chatFirst = true,
}) {
  const missing = useMemo(() => status?.missing || [], [status?.missing]);
  const providers = useMemo(() => status?.providers || [], [status?.providers]);
  const tasks = useMemo(() => status?.tasks || [], [status?.tasks]);
  const pendingTasks = useMemo(
    () => tasks.filter((task) => task.status !== "completed"),
    [tasks],
  );
  const [selectedProvider, setSelectedProvider] = useState("");
  const [secretValue, setSecretValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const providerItems = tasks.length ? tasks : providers;
  useEffect(() => {
    if (!providerItems.length) {
      setSelectedProvider("");
      return;
    }
    setSelectedProvider((current) => (
      providerItems.some((item) => item.provider === current)
        ? current
        : (pendingTasks[0]?.provider || missing[0]?.provider || providerItems[0].provider)
    ));
  }, [missing, pendingTasks, providerItems]);

  if (!status?.visible || (status.ready && !pendingTasks.length)) {
    return null;
  }

  const summary = status.summary || {};
  const canManage = Boolean(status.can_manage_credentials);
  const blocking = Boolean(
    status.platform_mode === "blocked_launch"
      || status.execution_policy?.normal_prompts_allowed === false,
  );
  const blockedAgents = status.blocked_resources?.agents || [];
  const blockedTools = status.blocked_resources?.tools || [];
  const primary = providerItems.find((item) => item.provider === selectedProvider)
    || pendingTasks[0]
    || missing[0]
    || providerItems[0];
  const primaryEnv = primary?.required_env || primary?.required_envs?.[0] || "";
  const primaryConfigured = Boolean(primary?.configured || primary?.status === "completed");

  if (chatFirst) {
    const readyProviders = providers.filter((provider) => provider.configured || provider.status === "completed");
    return (
      <Paper
        elevation={0}
        sx={{
          maxWidth: 1120,
          mx: "auto",
          mb: 1.5,
          p: { xs: 1.5, md: 1.75 },
          borderRadius: 3,
          border: "1px solid rgba(184,115,51,0.20)",
          bgcolor: "rgba(255,255,255,0.78)",
          boxShadow: "0 16px 46px rgba(20,18,15,0.06)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.25}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: blocking ? "rgba(255,152,0,0.12)" : "rgba(184,115,51,0.10)",
                color: copperDark,
                flexShrink: 0,
              }}
            >
              <VpnKeyIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                <Typography fontWeight={950}>Casper Setup</Typography>
                <Chip
                  size="small"
                  label={`${status.completion_percent || 0}% ready`}
                  sx={{ height: 24, bgcolor: "rgba(184,115,51,0.10)", color: copperDark, fontWeight: 850 }}
                />
                {blocking ? (
                  <Chip
                    size="small"
                    label="Action required"
                    sx={{ height: 24, bgcolor: "rgba(255,152,0,0.13)", color: "#9A5A00", fontWeight: 850 }}
                  />
                ) : (
                  <Chip
                    size="small"
                    label={pendingTasks.length ? "Optional setup" : "Ready"}
                    sx={{
                      height: 24,
                      bgcolor: pendingTasks.length ? "rgba(184,115,51,0.10)" : "rgba(67,120,72,0.10)",
                      color: pendingTasks.length ? copperDark : "#437848",
                      fontWeight: 850,
                    }}
                  />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {blocking
                  ? "Casper will guide required setup before normal execution."
                  : pendingTasks.length
                    ? "Casper is ready; optional services can be connected when needed."
                    : "All currently required services are ready."}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
            {readyProviders.slice(0, 3).map((provider) => (
              <Chip
                key={provider.provider}
                size="small"
                icon={<CheckIcon />}
                label={`${provider.provider_label} · ready`}
                sx={{
                  height: 30,
                  bgcolor: "rgba(67,120,72,0.10)",
                  color: "#437848",
                  fontWeight: 850,
                  "& .MuiChip-icon": { color: "#437848" },
                }}
              />
            ))}
            {pendingTasks.slice(0, 2).map((task) => (
              <Chip
                key={task.id || task.provider}
                size="small"
                icon={<VpnKeyIcon />}
                label={`${task.provider_label} · setup`}
                sx={{
                  height: 30,
                  bgcolor: "rgba(246,240,235,0.96)",
                  color: copperDark,
                  fontWeight: 850,
                  "& .MuiChip-icon": { color: copperDark },
                }}
              />
            ))}
            <Button
              type="button"
              variant="contained"
              onClick={onStartGuide}
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 900,
                bgcolor: copper,
                px: 2,
                "&:hover": { bgcolor: copperDark },
              }}
            >
              {blocking ? "Start setup" : "Setup guide"}
            </Button>
            <Tooltip title="Refresh setup">
              <IconButton onClick={onRefresh} disabled={loading} sx={{ color: copperDark }}>
                {loading ? <CircularProgress size={18} sx={{ color: copper }} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
            {!blocking && (
              <Tooltip title="Dismiss">
                <IconButton onClick={onDismiss} sx={{ color: "text.secondary" }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  }

  const handleSaveCredential = async () => {
    if (!primary || !secretValue.trim()) return;
    setSaving(true);
    setSaveError("");
    try {
      await createKey({
        name: primaryEnv || `${primary.provider}_api_key`,
        provider: primary.provider_label || primary.provider,
        key_type: "api_key",
        value: secretValue.trim(),
        description: primaryEnv
          ? `Credential used by active Next Moca tools and agents that require ${primaryEnv}.`
          : `Credential used by active Next Moca tools and agents for ${primary.provider_label || primary.provider}.`,
      });
      setSecretValue("");
      window.dispatchEvent(new CustomEvent("nextmoca:credentials-updated"));
      await onRefresh?.();
    } catch (error) {
      setSaveError(error?.message || "Could not save this credential.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 1120,
        mx: "auto",
        mb: 2.5,
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        border: "1px solid rgba(184,115,51,0.22)",
        bgcolor: "rgba(255,255,255,0.86)",
        boxShadow: "0 18px 52px rgba(20,18,15,0.08)",
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "flex-start" }}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 2.2,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(184,115,51,0.10)",
            color: copperDark,
            flexShrink: 0,
          }}
        >
          <VpnKeyIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.75 }}>
            <Typography variant="h6" fontWeight={900}>
              Casper Setup Center
            </Typography>
            <Chip
              size="small"
              label={`${status.completion_percent || 0}% ready`}
              sx={{
                height: 26,
                bgcolor: "rgba(184,115,51,0.10)",
                color: copperDark,
                fontWeight: 850,
              }}
            />
            {blocking && (
              <Chip
                size="small"
                label="Launch paused"
                sx={{
                  height: 26,
                  bgcolor: "rgba(255,152,0,0.12)",
                  color: "#9A5A00",
                  fontWeight: 850,
                }}
              />
            )}
            {!blocking && pendingTasks.length > 0 && (
              <Chip
                size="small"
                label="Optional setup"
                sx={{
                  height: 26,
                  bgcolor: "rgba(184,115,51,0.10)",
                  color: copperDark,
                  fontWeight: 850,
                }}
              />
            )}
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.4 }}>
            {blocking
              ? "Finish required setup before Casper serves normal prompts or runs dependent resources."
              : "Casper is usable. Connect optional services when you need their tools."}
            {" "}
            {pendingTasks.length} optional task{pendingTasks.length === 1 ? "" : "s"} lock {summary.blocked_tools || 0} tool{summary.blocked_tools === 1 ? "" : "s"}
            {summary.blocked_agents ? ` and ${summary.blocked_agents} agent${summary.blocked_agents === 1 ? "" : "s"}` : ""}.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
          {providerItems.map((item) => {
            const configured = Boolean(item.configured || item.status === "completed");
            return (
            <Chip
              key={item.id || item.provider}
              size="small"
              icon={configured ? <CheckIcon /> : <VpnKeyIcon />}
              label={`${item.provider_label} · ${configured ? "ready" : "setup"}`}
              onClick={() => setSelectedProvider(item.provider)}
              sx={{
                height: 34,
                borderRadius: "999px",
                bgcolor: item.provider === selectedProvider
                  ? (configured ? "rgba(76,175,80,0.14)" : "rgba(184,115,51,0.14)")
                  : "rgba(246,240,235,0.96)",
                border: item.provider === selectedProvider
                  ? `1px solid ${configured ? "rgba(76,175,80,0.38)" : "rgba(184,115,51,0.44)"}`
                  : "1px solid transparent",
                color: configured ? "#2E7D32" : copperDark,
                fontWeight: 850,
                "& .MuiChip-icon": { color: configured ? "#2E7D32" : copperDark },
              }}
            />
            );
          })}
          {!canManage && (
            <Chip
              size="small"
              label="Ask an admin"
              sx={{ height: 34, color: copperDark, bgcolor: "rgba(246,240,235,0.96)", fontWeight: 850 }}
            />
          )}
          <Tooltip title="Refresh setup">
            <IconButton onClick={onRefresh} disabled={loading} sx={{ color: copperDark }}>
              {loading ? <CircularProgress size={18} sx={{ color: copper }} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          {!blocking && (
            <Tooltip title="Dismiss">
              <IconButton onClick={onDismiss} sx={{ color: "text.secondary" }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      {pendingTasks.length > 0 && (
        <Grid container spacing={1.25} sx={{ mt: 2, ml: { md: "62px" }, width: { md: "calc(100% - 62px)" } }}>
          {pendingTasks.slice(0, 4).map((task) => (
            <Grid item xs={12} md={6} key={task.id || task.provider}>
              <Paper
                elevation={0}
                onClick={() => setSelectedProvider(task.provider)}
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  cursor: "pointer",
                  border: task.provider === selectedProvider
                    ? "1px solid rgba(184,115,51,0.42)"
                    : "1px solid rgba(184,115,51,0.16)",
                  bgcolor: "rgba(255,255,255,0.74)",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <VpnKeyIcon sx={{ color: copperDark, mt: 0.2 }} fontSize="small" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={900} sx={{ color: copperDark }}>
                      {task.title || `Connect ${task.provider_label}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      Unlocks {task.unlocks?.tools || 0} tool{task.unlocks?.tools === 1 ? "" : "s"}
                      {task.unlocks?.agents ? ` and ${task.unlocks.agents} agent${task.unlocks.agents === 1 ? "" : "s"}` : ""}.
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.75, rowGap: 0.6 }}>
                      {(task.sample_tools || []).slice(0, 2).map((name) => (
                        <Chip key={name} size="small" label={name} sx={{ height: 22, fontWeight: 750 }} />
                      ))}
                      {(task.sample_agents || []).slice(0, 2).map((name) => (
                        <Chip key={name} size="small" label={name} sx={{ height: 22, fontWeight: 750 }} />
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
      {canManage && primary && (
        <Box
          sx={{
            mt: 2,
            ml: { md: "62px" },
            p: 1.25,
            borderRadius: 2.5,
            bgcolor: "rgba(246,240,235,0.48)",
            border: "1px solid rgba(184,115,51,0.14)",
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ xs: "stretch", md: "center" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={850} color={copperDark}>
                {primaryConfigured ? `${primary.provider_label} is ready` : `Add ${primary.provider_label}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {primaryConfigured
                  ? `${primaryEnv || "Credential"} is already available from ${primary.configured_by === "environment" ? "the deployment environment" : "Key Manager"}.`
                  : `${primaryEnv || "API key"} is used by ${primary.sample_tools?.[0] || primary.sample_agents?.[0] || "active runtime capabilities"}.`}
              </Typography>
            </Box>
            {!primaryConfigured && (
              <>
                <TextField
                  size="small"
                  type="password"
                  value={secretValue}
                  onChange={(event) => setSecretValue(event.target.value)}
                  placeholder={primaryEnv || "Paste API key"}
                  autoComplete="off"
                  sx={{
                    minWidth: { xs: "100%", md: 360 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "999px",
                      bgcolor: "#fff",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveCredential}
                  disabled={saving || !secretValue.trim()}
                  sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 900, px: 2.25 }}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
              </>
            )}
            {primary.setup_url && (
              <Button
                component="a"
                href={primary.setup_url}
                sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 850, color: copperDark }}
              >
                Advanced
              </Button>
            )}
          </Stack>
          {saveError && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
              {saveError}
            </Typography>
          )}
        </Box>
      )}
      {(blockedAgents.length > 0 || blockedTools.length > 0) && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            mt: 1.25,
            ml: { md: "62px" },
            bgcolor: "transparent",
            border: "1px solid rgba(184,115,51,0.14)",
            borderRadius: "16px !important",
            overflow: "hidden",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: copperDark }} />}>
            <Typography fontWeight={850} color={copperDark}>
              Locked resources ({blockedAgents.length + blockedTools.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
              {[...blockedAgents, ...blockedTools].slice(0, 12).map((resource) => (
                <Chip
                  key={`${resource.type}:${resource.id || resource.name}:${resource.version || ""}`}
                  size="small"
                  label={`${resource.type}: ${resource.display_name || resource.name}`}
                  sx={{
                    bgcolor: "rgba(246,240,235,0.92)",
                    color: copperDark,
                    fontWeight: 750,
                  }}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  );
}

function CasperOnboardingGuideMessage({
  status,
  loading,
  skippedTaskIds,
  onSaveCredential,
  onSkipTask,
  onResetSkipped,
  onRefresh,
}) {
  const allPendingTasks = useMemo(() => getAllPendingOnboardingTasks(status), [status]);
  const pendingTasks = useMemo(
    () => getPendingOnboardingTasks(status, skippedTaskIds),
    [skippedTaskIds, status],
  );
  const skipped = useMemo(() => new Set((skippedTaskIds || []).map(String)), [skippedTaskIds]);
  const skippedOptionalTasks = useMemo(
    () => allPendingTasks.filter((task) => !task.blocks_launch && skipped.has(onboardingTaskStorageId(task))),
    [allPendingTasks, skipped],
  );
  const readyProviders = useMemo(
    () => (status?.providers || []).filter((provider) => provider.configured || provider.status === "completed"),
    [status?.providers],
  );
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [secretValue, setSecretValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    const firstTaskId = pendingTasks[0] ? onboardingTaskStorageId(pendingTasks[0]) : "";
    if (!pendingTasks.length) {
      setSelectedTaskId("");
      setSecretValue("");
      return;
    }
    setSelectedTaskId((current) => (
      pendingTasks.some((task) => onboardingTaskStorageId(task) === current)
        ? current
        : firstTaskId
    ));
  }, [pendingTasks]);

  if (!status?.visible) {
    return (
      <Stack spacing={1}>
        <Typography variant="subtitle1" fontWeight={950}>
          Setup is not required right now.
        </Typography>
        <Typography color="text.secondary">
          Casper did not find required platform setup tasks for this deployment.
        </Typography>
      </Stack>
    );
  }

  const selectedTask = pendingTasks.find((task) => onboardingTaskStorageId(task) === selectedTaskId) || pendingTasks[0];
  const selectedEnv = selectedTask?.required_env || selectedTask?.required_envs?.[0] || "";
  const canManage = Boolean(status.can_manage_credentials);
  const blocking = Boolean(
    status.platform_mode === "blocked_launch"
      || status.execution_policy?.normal_prompts_allowed === false,
  );

  const handleSave = async () => {
    if (!selectedTask || !secretValue.trim()) return;
    setSaving(true);
    setNotice("");
    setSaveError("");
    try {
      await onSaveCredential?.(selectedTask, secretValue.trim());
      setSecretValue("");
      setNotice(`${selectedTask.provider_label || "Service"} is saved. I’m checking readiness again.`);
    } catch (error) {
      setSaveError(error?.message || "Could not save this credential.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <CasperGhost size={34} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
            <Typography variant="h6" fontWeight={950}>
              Let’s finish setup together
            </Typography>
            <Chip
              size="small"
              label={`${status.completion_percent || 0}% ready`}
              sx={{ height: 24, bgcolor: "rgba(184,115,51,0.10)", color: copperDark, fontWeight: 850 }}
            />
            <Chip
              size="small"
              label={blocking ? "Required before launch" : "Optional connectors"}
              sx={{
                height: 24,
                bgcolor: blocking ? "rgba(255,152,0,0.12)" : "rgba(67,120,72,0.10)",
                color: blocking ? "#9A5A00" : "#437848",
                fontWeight: 850,
              }}
            />
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.35 }}>
            {blocking
              ? "I’ll only allow setup questions until required services are ready. This prevents agents and tools from failing later."
              : "Core Casper is ready. You can connect optional services now or skip them until you need those tools."}
          </Typography>
        </Box>
      </Stack>

      {readyProviders.length > 0 && (
        <Box>
          <Typography variant="caption" fontWeight={900} color="text.secondary">
            Ready services
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.65 }}>
            {readyProviders.map((provider) => (
              <Chip
                key={provider.provider}
                size="small"
                icon={<CheckIcon />}
                label={`${provider.provider_label} · ${onboardingCredentialOriginLabel(provider)}`}
                sx={{
                  height: 30,
                  bgcolor: "rgba(67,120,72,0.10)",
                  color: "#437848",
                  fontWeight: 800,
                  "& .MuiChip-icon": { color: "#437848" },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {selectedTask ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.4, md: 1.6 },
            borderRadius: 2.5,
            border: "1px solid rgba(184,115,51,0.18)",
            bgcolor: "rgba(246,240,235,0.44)",
          }}
        >
          <Stack spacing={1.2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between">
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={950} color={copperDark}>
                  {selectedTask.title || `Connect ${selectedTask.provider_label}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedEnv || "Credential"} unlocks {selectedTask.unlocks?.tools || 0} tool{selectedTask.unlocks?.tools === 1 ? "" : "s"}
                  {selectedTask.unlocks?.agents ? ` and ${selectedTask.unlocks.agents} agent${selectedTask.unlocks.agents === 1 ? "" : "s"}` : ""}.
                </Typography>
              </Box>
              {pendingTasks.length > 1 && (
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                  {pendingTasks.map((task) => {
                    const taskId = onboardingTaskStorageId(task);
                    return (
                      <Chip
                        key={taskId}
                        size="small"
                        label={task.provider_label}
                        onClick={() => {
                          setSelectedTaskId(taskId);
                          setSecretValue("");
                          setNotice("");
                          setSaveError("");
                        }}
                        sx={{
                          height: 28,
                          bgcolor: taskId === selectedTaskId ? "rgba(184,115,51,0.15)" : "rgba(255,255,255,0.74)",
                          color: copperDark,
                          border: taskId === selectedTaskId ? "1px solid rgba(184,115,51,0.38)" : "1px solid rgba(184,115,51,0.14)",
                          fontWeight: 800,
                        }}
                      />
                    );
                  })}
                </Stack>
              )}
            </Stack>

            {Boolean((selectedTask.sample_tools?.length || 0) + (selectedTask.sample_agents?.length || 0)) && (
              <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap>
                {(selectedTask.sample_agents || []).slice(0, 3).map((name) => (
                  <Chip key={`agent-${name}`} size="small" label={name} sx={{ height: 24, fontWeight: 750 }} />
                ))}
                {(selectedTask.sample_tools || []).slice(0, 4).map((name) => (
                  <Chip key={`tool-${name}`} size="small" label={name} sx={{ height: 24, fontWeight: 750 }} />
                ))}
              </Stack>
            )}

            {canManage ? (
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }}>
                <TextField
                  size="small"
                  type="password"
                  value={secretValue}
                  onChange={(event) => {
                    setSecretValue(event.target.value);
                    setSaveError("");
                    setNotice("");
                  }}
                  placeholder={selectedEnv || "Paste API key"}
                  autoComplete="off"
                  sx={{
                    flex: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "999px",
                      bgcolor: "#fff",
                      "& fieldset": { borderColor: "rgba(184,115,51,0.18)" },
                      "&:hover fieldset": { borderColor: "rgba(184,115,51,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: copper },
                    },
                  }}
                />
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || loading || !secretValue.trim()}
                  sx={{
                    borderRadius: "999px",
                    textTransform: "none",
                    fontWeight: 900,
                    px: 2.5,
                    bgcolor: copper,
                    "&:hover": { bgcolor: copperDark },
                  }}
                >
                  {saving ? "Saving..." : "Save key"}
                </Button>
                {!selectedTask.blocks_launch && (
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => onSkipTask?.(selectedTask)}
                    disabled={saving || loading}
                    sx={{
                      borderRadius: "999px",
                      textTransform: "none",
                      fontWeight: 850,
                      borderColor: "rgba(184,115,51,0.30)",
                      color: copperDark,
                    }}
                  >
                    Skip for now
                  </Button>
                )}
                {selectedTask.setup_url && (
                  <Button
                    component="a"
                    href={selectedTask.setup_url}
                    sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 850, color: copperDark }}
                  >
                    Advanced
                  </Button>
                )}
              </Stack>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Ask an admin with Key Manager access to add {selectedEnv || "this credential"}.
              </Alert>
            )}

            {(notice || saveError) && (
              <Alert severity={saveError ? "error" : "success"} sx={{ borderRadius: 2 }}>
                {saveError || notice}
              </Alert>
            )}
          </Stack>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 1.6,
            borderRadius: 2.5,
            border: "1px solid rgba(67,120,72,0.20)",
            bgcolor: "rgba(238,248,239,0.70)",
          }}
        >
          <Typography fontWeight={950} color="#2F6F39">
            Required setup is complete.
          </Typography>
          {skippedOptionalTasks.length > 0 ? (
            <Stack spacing={1} sx={{ mt: 0.6 }}>
              <Typography variant="body2" color="text.secondary">
                You skipped {skippedOptionalTasks.length} optional service{skippedOptionalTasks.length === 1 ? "" : "s"}. You can bring them back whenever you need those tools.
              </Typography>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={onResetSkipped}
                sx={{
                  alignSelf: "flex-start",
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 850,
                  borderColor: "rgba(184,115,51,0.30)",
                  color: copperDark,
                }}
              >
                Show skipped services
              </Button>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              Casper can now answer setup questions and run eligible agents, tools, workflows, and knowledge searches.
            </Typography>
          )}
        </Paper>
      )}

      <Stack direction="row" spacing={0.75} alignItems="center">
        <Button
          type="button"
          variant="text"
          size="small"
          onClick={onRefresh}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          sx={{ color: copperDark, fontWeight: 850, textTransform: "none" }}
        >
          Refresh readiness
        </Button>
        <Typography variant="caption" color="text.secondary">
          Secrets are saved to Key Manager and never shown in chat history.
        </Typography>
      </Stack>
    </Stack>
  );
}

function artifactFileName(artifact) {
  return artifact?.filename || artifact?.name || "artifact";
}

function isImageArtifact(artifact) {
  const name = artifactFileName(artifact);
  const mimeType = artifact?.content_type || artifact?.mime_type || artifact?.type || "";
  return mimeType.startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(name);
}

function InlineArtifactCard({ artifact, runContext, onDownload }) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewError, setPreviewError] = useState("");
  const filename = artifactFileName(artifact);
  const artifactRunContext = artifact?.run_context || artifact?.runContext || runContext;
  const canPreview = isImageArtifact(artifact) && artifact?.filename && artifactRunContext;

  useEffect(() => {
    let active = true;
    let objectUrl = "";
    setPreviewUrl("");
    setPreviewError("");

    if (!canPreview) {
      return () => {};
    }

    downloadArtifactFile(
      artifactRunContext.agentId,
      artifactRunContext.version,
      artifactRunContext.runNumber,
      artifact.filename,
    )
      .then(async (response) => {
        if (!response?.ok) {
          throw new Error("Preview failed");
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (active) {
          setPreviewUrl(objectUrl);
        }
      })
      .catch(() => {
        if (active) {
          setPreviewError("Preview unavailable");
        }
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [artifact?.filename, canPreview, artifactRunContext?.agentId, artifactRunContext?.runNumber, artifactRunContext?.version]);

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: "hidden",
        border: "1px solid rgba(184,115,51,0.24)",
        borderRadius: 2,
        bgcolor: "rgba(255,255,255,0.84)",
        boxShadow: "0 16px 42px rgba(20,18,15,0.07)",
      }}
    >
      {canPreview && previewUrl && (
        <Box
          component="img"
          src={previewUrl}
          alt={filename}
          sx={{
            display: "block",
            width: "100%",
            maxHeight: 420,
            objectFit: "cover",
            bgcolor: "rgba(20,18,15,0.04)",
          }}
        />
      )}
      {canPreview && !previewUrl && !previewError && (
        <Box
          sx={{
            height: 180,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(246,240,235,0.72)",
          }}
        >
          <CircularProgress size={24} sx={{ color: copper }} />
        </Box>
      )}
      {!canPreview && (
        <Box
          sx={{
            height: 96,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(246,240,235,0.72)",
            color: copperDark,
          }}
        >
          <Inventory2OutlinedIcon />
        </Box>
      )}
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ p: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography fontWeight={850} noWrap>
            {filename}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {previewError || "Ready"}
          </Typography>
        </Box>
        <Tooltip title="Download">
          <IconButton
            aria-label={`Download ${filename}`}
            onClick={() => onDownload(artifact, artifactRunContext)}
            sx={{ color: copperDark }}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

function InlineArtifactGallery({ artifacts, loading, runContext, onDownload }) {
  if (!loading && !artifacts.length) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: 760, width: "100%", alignSelf: "flex-start" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25, color: copperDark }}>
        <Inventory2OutlinedIcon fontSize="small" />
        <Typography variant="subtitle2" fontWeight={900}>
          Created files
        </Typography>
      </Stack>
      {loading ? (
        <Paper
          elevation={0}
          sx={{
            px: 2,
            py: 1.5,
            border: "1px solid rgba(184,115,51,0.22)",
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.72)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <CircularProgress size={18} sx={{ color: copper }} />
            <Typography color="text.secondary">Preparing files...</Typography>
          </Stack>
        </Paper>
      ) : (
        <Grid container spacing={1.5}>
          {artifacts.map((artifact) => (
            <Grid item xs={12} md={artifacts.length === 1 ? 12 : 6} key={artifact.id || artifact.filename || artifact.name}>
              <InlineArtifactCard artifact={artifact} runContext={runContext} onDownload={onDownload} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

function MessageArtifactGallery({ message, sessionData, onDownload }) {
  const [messageArtifacts, setMessageArtifacts] = useState([]);
  const [messageArtifactsLoading, setMessageArtifactsLoading] = useState(false);
  const session = sessionData?.session || {};
  const runContext = session?.agent_id && message?.run_number
    ? {
      agentId: session.agent_id,
      version: message.agent_version || session.agent_version,
      runNumber: message.run_number,
    }
    : null;

  useEffect(() => {
    let active = true;
    setMessageArtifacts([]);
    if (!runContext?.agentId || !runContext?.version || !runContext?.runNumber) {
      setMessageArtifactsLoading(false);
      return () => {};
    }
    setMessageArtifactsLoading(true);
    getAgentRunArtifacts(runContext.agentId, runContext.version, runContext.runNumber)
      .then((data) => {
        if (active) {
          setMessageArtifacts(data?.artifacts || []);
        }
      })
      .catch(() => {
        if (active) {
          setMessageArtifacts([]);
        }
      })
      .finally(() => {
        if (active) {
          setMessageArtifactsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [message.status, runContext?.agentId, runContext?.runNumber, runContext?.version]);

  if (!messageArtifactsLoading && !messageArtifacts.length) {
    return null;
  }

  return (
    <Box sx={{ mt: 1.6 }}>
      <InlineArtifactGallery
        artifacts={messageArtifacts}
        loading={messageArtifactsLoading}
        runContext={runContext}
        onDownload={(artifact) => onDownload(artifact, runContext)}
      />
    </Box>
  );
}

function PromptAttachmentPreview({ attachments = [], onRemove = null }) {
  const visibleAttachments = Array.isArray(attachments) ? attachments.filter(Boolean) : [];
  if (!visibleAttachments.length) return null;

  return (
    <Stack spacing={0.9} sx={{ mb: 1.1 }}>
      {visibleAttachments.map((attachment) => {
        const key = attachment.id || attachment.preview_url || attachment.relative_path || attachment.name;
        const isImage = isImageAttachment(attachment);
        return (
          <Paper
            key={key}
            elevation={0}
            sx={{
              overflow: "hidden",
              border: "1px solid rgba(184,115,51,0.18)",
              borderRadius: 2,
              bgcolor: "rgba(255,252,249,0.86)",
            }}
          >
            {isImage && attachment.preview_url && (
              <Box
                component="img"
                src={attachment.preview_url}
                alt={attachment.name || "Uploaded image"}
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: 260,
                  objectFit: "contain",
                  bgcolor: "rgba(20,18,15,0.03)",
                }}
              />
            )}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 1.2, py: 0.9 }}
            >
              <UploadFileIcon sx={{ color: copperDark, fontSize: 18 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={850} noWrap>
                  {attachment.name || attachment.filename || "Uploaded file"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isImage ? "Image attached to this prompt" : "File attached to this prompt"}
                </Typography>
              </Box>
              {onRemove && (
                <Tooltip title="Remove from next prompt">
                  <IconButton
                    size="small"
                    onClick={() => onRemove(attachment)}
                    sx={{ color: "text.secondary" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

export default function CasperWorkspace() {
  const { user, can } = useUser();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const casperSurface = isDarkMode ? alpha("#211B16", 0.92) : "rgba(255,255,255,0.82)";
  const casperUserSurface = isDarkMode ? alpha("#2A211B", 0.94) : "rgba(255,255,255,0.9)";
  const casperSoftSurface = isDarkMode ? alpha("#2A211B", 0.72) : "rgba(246,240,235,0.68)";
  const casperBorder = isDarkMode ? "rgba(224,177,132,0.26)" : "rgba(184,115,51,0.30)";
  const casperCopperText = isDarkMode ? "#F2C69B" : copperDark;
  const [profile, setProfile] = useState(null);
  const [intent, setIntent] = useState("");
  const [resolvedIntent, setResolvedIntent] = useState("");
  const [resolution, setResolution] = useState(null);
  const [routePickerOpen, setRoutePickerOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showIntro, setShowIntro] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [artifactsOpen, setArtifactsOpen] = useState(false);
  const [artifacts, setArtifacts] = useState([]);
  const [artifactsLoading, setArtifactsLoading] = useState(false);
  const [artifactError, setArtifactError] = useState("");
  const [selectedSession, setSelectedSession] = useState(null);
  const [executionNotice, setExecutionNotice] = useState(null);
  const [authRequired, setAuthRequired] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadNotice, setUploadNotice] = useState("");
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [pendingPromptFiles, setPendingPromptFiles] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [workflowApprovalFeedback, setWorkflowApprovalFeedback] = useState({});
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [setupDrawerOpen, setSetupDrawerOpen] = useState(false);
  const [onboardingReviewedTaskIds, setOnboardingReviewedTaskIds] = useState([]);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [onboardingConversation, setOnboardingConversation] = useState({
    phase: "idle",
    activeTaskId: "",
    draftUser: {},
  });
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const streamCleanupRef = useRef(null);
  const workflowPollersRef = useRef({});
  const chatEndRef = useRef(null);
  const dragDepthRef = useRef(0);
  const restoredSessionKeyRef = useRef(null);
  const uploadPreviewUrlsRef = useRef(new Set());
  const setupDrawerAutoOpenedRef = useRef("");
  const activeSessionStorageKey = useMemo(() => {
    const identity = user?.id
      || user?.username
      || profile?.user?.id
      || profile?.user?.username;
    if (!identity) return null;
    return `${CASPER_ACTIVE_SESSION_PREFIX}:${identity}`;
  }, [profile?.user?.id, profile?.user?.username, user?.id, user?.username]);
  const localTranscriptStorageKey = useMemo(() => {
    const identity = user?.id
      || user?.username
      || profile?.user?.id
      || profile?.user?.username;
    if (!identity) return null;
    return `${CASPER_LOCAL_TRANSCRIPT_PREFIX}:${identity}`;
  }, [profile?.user?.id, profile?.user?.username, user?.id, user?.username]);
  const onboardingSkippedStorageKey = useMemo(() => {
    const identity = user?.id
      || user?.username
      || profile?.user?.id
      || profile?.user?.username;
    if (!identity) return null;
    return `${CASPER_ONBOARDING_SKIPPED_PREFIX}:${identity}`;
  }, [profile?.user?.id, profile?.user?.username, user?.id, user?.username]);
  const onboardingReviewedStorageKey = useMemo(() => {
    const identity = user?.id
      || user?.username
      || profile?.user?.id
      || profile?.user?.username;
    if (!identity) return null;
    return `${CASPER_ONBOARDING_REVIEWED_PREFIX}:${identity}`;
  }, [profile?.user?.id, profile?.user?.username, user?.id, user?.username]);
  const onboardingCompleteStorageKey = useMemo(() => {
    const identity = user?.id
      || user?.username
      || profile?.user?.id
      || profile?.user?.username;
    if (!identity) return null;
    return `${CASPER_ONBOARDING_COMPLETE_PREFIX}:${identity}`;
  }, [profile?.user?.id, profile?.user?.username, user?.id, user?.username]);
  const onboardingFingerprint = useMemo(
    () => onboardingReadinessFingerprint(onboardingStatus),
    [onboardingStatus],
  );
  const [onboardingSkippedTasks, setOnboardingSkippedTasks] = useState([]);

  useEffect(() => () => {
    uploadPreviewUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // Object URL cleanup is best-effort.
      }
    });
    uploadPreviewUrlsRef.current.clear();
  }, []);

  const workflowMessageContext = messageHasWorkflowContext;

  const latestWorkflowRunInfoFromMessages = (messages = []) => {
    const workflowMessage = [...messages].reverse().find((message) => (
      message?.metadata?.workflow_run?.workflow_id
      && message?.metadata?.workflow_run?.version
      && message?.metadata?.workflow_run?.run_number
    ));
    return workflowMessage?.metadata?.workflow_run || null;
  };

  const isWorkflowTerminalStatus = (status) => {
    const normalized = String(status?.status || status?.workflow_status || "").toLowerCase();
    return ["success", "completed", "failed", "error", "cancelled", "rejected"].includes(normalized);
  };

  const sanitizeAttachmentForStorage = (attachment) => {
    if (!attachment || typeof attachment !== "object") return attachment;
    const next = { ...attachment };
    if (typeof next.preview_url === "string" && next.preview_url.startsWith("blob:")) {
      delete next.preview_url;
    }
    return next;
  };

  const sanitizeMessageForStorage = (message) => {
    const metadata = message?.metadata && typeof message.metadata === "object"
      ? { ...message.metadata }
      : message?.metadata;
    if (metadata && typeof metadata === "object") {
      if (Array.isArray(metadata.attachments)) {
        metadata.attachments = metadata.attachments.map(sanitizeAttachmentForStorage);
      }
      if (Array.isArray(metadata.files)) {
        metadata.files = metadata.files.map(sanitizeAttachmentForStorage);
      }
    }
    return {
      ...message,
      attachments: Array.isArray(message?.attachments)
        ? message.attachments.map(sanitizeAttachmentForStorage)
        : message?.attachments,
      metadata,
      feedbackPending: false,
    };
  };

  const persistActiveSessionId = useCallback((sessionId) => {
    if (!sessionId || !activeSessionStorageKey) return;
    try {
      window.localStorage.setItem(activeSessionStorageKey, String(sessionId));
    } catch {
      // Browser storage can be unavailable in private contexts; Casper should still work.
    }
  }, [activeSessionStorageKey]);

  const readActiveSessionId = useCallback(() => {
    if (!activeSessionStorageKey) return null;
    try {
      return window.localStorage.getItem(activeSessionStorageKey);
    } catch {
      return null;
    }
  }, [activeSessionStorageKey]);

  const clearActiveSessionId = useCallback(() => {
    if (!activeSessionStorageKey) return;
    try {
      window.localStorage.removeItem(activeSessionStorageKey);
    } catch {
      // Browser storage can be unavailable in private contexts; Casper should still work.
    }
  }, [activeSessionStorageKey]);

  const readLocalWorkflowTranscript = useCallback(() => {
    if (!localTranscriptStorageKey) return null;
    try {
      const raw = window.sessionStorage.getItem(localTranscriptStorageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed?.messages) || parsed.messages.length === 0) return null;
      return parsed;
    } catch {
      return null;
    }
  }, [localTranscriptStorageKey]);

  const clearLocalWorkflowTranscript = useCallback(() => {
    if (!localTranscriptStorageKey) return;
    try {
      window.sessionStorage.removeItem(localTranscriptStorageKey);
      window.localStorage.removeItem(localTranscriptStorageKey);
    } catch {
      // Local storage can be unavailable in private contexts; Casper should still work.
    }
  }, [localTranscriptStorageKey]);

  useEffect(() => {
    if (!localTranscriptStorageKey) return;
    if (!chatMessages.length) return;
    if (!shouldPersistCasperLocalTranscript(chatMessages)) {
      clearLocalWorkflowTranscript();
      return;
    }
    const workflowRun = latestWorkflowRunInfoFromMessages(chatMessages);
    try {
      window.sessionStorage.setItem(
        localTranscriptStorageKey,
        JSON.stringify({
          app_boot_id: casperAppBootId,
          updated_at: new Date().toISOString(),
          session_id: selectedSession?.session?.id || null,
          workflow_run: workflowRun,
          messages: chatMessages.map(sanitizeMessageForStorage),
        }),
      );
    } catch {
      // Local storage can be unavailable in private contexts; Casper should still work.
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessages, clearLocalWorkflowTranscript, localTranscriptStorageKey, selectedSession?.session?.id]);

  const refreshSessions = async () => {
    try {
      const data = await listCasperSessions(200, 200);
      setSessions(data?.sessions || []);
    } catch {
      // Session history is a convenience surface; never block Casper.
    }
  };

  const refreshOnboarding = useCallback(async () => {
    setOnboardingLoading(true);
    try {
      const data = await getCasperOnboardingStatus();
      setOnboardingStatus(data);
    } catch {
      setOnboardingStatus(null);
    } finally {
      setOnboardingLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    getCasperProfile()
      .then((data) => {
        if (mounted) setProfile(data);
      })
      .catch((err) => {
        if (mounted) setError(err.message || "Failed to load Casper profile");
      });
    refreshSessions();
    refreshOnboarding();
    const seen = sessionStorage.getItem("casper_intro_seen");
    if (!seen) {
      setShowIntro(true);
      sessionStorage.setItem("casper_intro_seen", "1");
      const timer = setTimeout(() => setShowIntro(false), 1650);
      return () => {
        mounted = false;
        clearTimeout(timer);
      };
    }
    return () => {
      mounted = false;
    };
  }, [refreshOnboarding]);

  useEffect(() => {
    if (!onboardingSkippedStorageKey) {
      setOnboardingSkippedTasks([]);
      return;
    }
    try {
      const raw = window.localStorage.getItem(onboardingSkippedStorageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      setOnboardingSkippedTasks(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      setOnboardingSkippedTasks([]);
    }
  }, [onboardingSkippedStorageKey]);

  useEffect(() => {
    if (!onboardingReviewedStorageKey) {
      setOnboardingReviewedTaskIds([]);
      return;
    }
    try {
      const raw = window.localStorage.getItem(onboardingReviewedStorageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      setOnboardingReviewedTaskIds(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      setOnboardingReviewedTaskIds([]);
    }
  }, [onboardingReviewedStorageKey]);

  useEffect(() => {
    if (!onboardingCompleteStorageKey) {
      setOnboardingComplete(false);
      return;
    }
    try {
      setOnboardingComplete(window.localStorage.getItem(onboardingCompleteStorageKey) === "1");
    } catch {
      setOnboardingComplete(false);
    }
  }, [onboardingCompleteStorageKey]);

  useEffect(() => {
    return () => {
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
        streamCleanupRef.current = null;
      }
      Object.values(workflowPollersRef.current || {}).forEach((poller) => {
        if (poller) clearInterval(poller);
      });
      workflowPollersRef.current = {};
    };
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [chatMessages.length, loading]);

  useEffect(() => {
    setChatMessages((current) => current.filter((message) => (
      message?.kind !== "onboarding" && message?.metadata?.kind !== "onboarding_guide"
    )));
  }, []);

  const entityAgents = resolution?.entities?.agents || EMPTY_ENTITIES;
  const entityWorkflows = resolution?.entities?.workflows || EMPTY_ENTITIES;
  const entityTools = resolution?.entities?.tools || EMPTY_ENTITIES;
  const entityKnowledge = resolution?.entities?.knowledge || EMPTY_ENTITIES;
  const allEntities = useMemo(
    () => [...entityAgents, ...entityWorkflows, ...entityTools, ...entityKnowledge],
    [entityAgents, entityWorkflows, entityTools, entityKnowledge],
  );
  const routePickerEntities = useMemo(
    () => rankedRoutePickerEntities(allEntities, { maxItems: 6, minScore: 0.25 }),
    [allEntities],
  );
  const showRoutePicker = shouldShowRoutePicker({
    routePickerOpen,
    entityCount: routePickerEntities.length,
  });
  const sessionMessageCount = useMemo(
    () => sessions.reduce((total, session) => total + Number(session.message_count || 0), 0),
    [sessions],
  );
  const sessionRunCount = useMemo(
    () => sessions.reduce((total, session) => total + Number(session.run_count || session.runs?.length || 0), 0),
    [sessions],
  );
  const canManageSetup = Boolean(
    onboardingStatus?.can_manage_credentials
      || can?.(PERMISSIONS.CONFIG_KEYS_MANAGE)
      || can?.(PERMISSIONS.TOOLS_CONFIGURE),
  );
  const setupAllPendingTasks = useMemo(
    () => getAllPendingOnboardingTasks(onboardingStatus),
    [onboardingStatus],
  );
  const setupPendingTasks = useMemo(
    () => getPendingOnboardingTasks(onboardingStatus, onboardingSkippedTasks),
    [onboardingSkippedTasks, onboardingStatus],
  );
  const setupReviewableTasks = useMemo(
    () => getReviewableOnboardingTasks(onboardingStatus, onboardingReviewedTaskIds),
    [onboardingReviewedTaskIds, onboardingStatus],
  );
  const setupBlocking = Boolean(
    onboardingStatus?.platform_mode === "blocked_launch"
      || onboardingStatus?.execution_policy?.normal_prompts_allowed === false,
  );
  const setupAlertVisible = Boolean(
    canManageSetup
      && onboardingStatus?.visible
      && setupAllPendingTasks.length > 0
      && onboardingComplete,
  );
  const setupAlertCount = setupPendingTasks.length || setupAllPendingTasks.length;

  const appendChatMessage = (message) => {
    setChatMessages((current) => [...current, message]);
    return message.id;
  };

  const removeChatMessage = (messageId) => {
    setChatMessages((current) => current.filter((message) => message.id !== messageId));
  };

  const updateChatMessage = (messageId, patch) => {
    setChatMessages((current) => current.map((message) => (
      message.id === messageId
        ? { ...message, ...(typeof patch === "function" ? patch(message) : patch) }
        : message
    )));
  };

  const persistOnboardingSkippedTasks = useCallback((taskIds) => {
    if (!onboardingSkippedStorageKey) return;
    try {
      window.localStorage.setItem(onboardingSkippedStorageKey, JSON.stringify(taskIds));
    } catch {
      // Local storage can be unavailable; skipped optional setup should never block use.
    }
  }, [onboardingSkippedStorageKey]);

  const persistOnboardingReviewedTasks = useCallback((taskIds) => {
    if (!onboardingReviewedStorageKey) return;
    try {
      window.localStorage.setItem(onboardingReviewedStorageKey, JSON.stringify(taskIds));
    } catch {
      // Reviewed setup state is a UX affordance; backend readiness remains authoritative.
    }
  }, [onboardingReviewedStorageKey]);

  const persistOnboardingComplete = useCallback((complete) => {
    if (!onboardingCompleteStorageKey) return;
    try {
      if (complete) {
        window.localStorage.setItem(onboardingCompleteStorageKey, "1");
      } else {
        window.localStorage.removeItem(onboardingCompleteStorageKey);
      }
    } catch {
      // If storage is unavailable, Casper can still complete the current session.
    }
  }, [onboardingCompleteStorageKey]);

  const skipOnboardingTask = useCallback((task) => {
    if (!task) return;
    const taskId = onboardingTaskStorageId(task);
    setOnboardingSkippedTasks((current) => {
      const next = Array.from(new Set([...current, taskId]));
      persistOnboardingSkippedTasks(next);
      return next;
    });
  }, [persistOnboardingSkippedTasks]);

  const resetSkippedOnboardingTasks = useCallback(() => {
    setOnboardingSkippedTasks([]);
    persistOnboardingSkippedTasks([]);
  }, [persistOnboardingSkippedTasks]);

  const rememberReviewedOnboardingTask = useCallback((task) => {
    if (!task) return onboardingReviewedTaskIds;
    const taskId = onboardingTaskStorageId(task);
    const next = Array.from(new Set([...onboardingReviewedTaskIds, taskId]));
    setOnboardingReviewedTaskIds(next);
    persistOnboardingReviewedTasks(next);
    return next;
  }, [onboardingReviewedTaskIds, persistOnboardingReviewedTasks]);

  const saveOnboardingCredential = useCallback(async (task, secret) => {
    const requiredEnv = task?.required_env || task?.required_envs?.[0] || "";
    await createKey({
      name: requiredEnv || `${task?.provider || "provider"}_api_key`,
      provider: task?.provider_label || task?.provider || "Provider",
      key_type: "api_key",
      value: secret,
      description: requiredEnv
        ? `Credential used by active Next Moca tools and agents that require ${requiredEnv}.`
        : `Credential used by active Next Moca tools and agents for ${task?.provider_label || task?.provider || "this provider"}.`,
    });
    window.dispatchEvent(new CustomEvent("nextmoca:credentials-updated"));
    const data = await getCasperOnboardingStatus();
    setOnboardingStatus(data);
    return data;
  }, []);

  const appendOnboardingGuideMessage = useCallback((statusOverride = null, content = "") => {
    const statusForGuide = statusOverride || onboardingStatus;
    if (!statusForGuide?.visible) return;
    setSetupDrawerOpen(true);
    if (content) {
      appendChatMessage(makeLocalMessage("system", content, { kind: "status" }));
    }
  }, [onboardingStatus]);

  useEffect(() => {
    if (!setupAlertVisible || loading || !canManageSetup) return;
    if (!setupBlocking) return;
    const fingerprint = [
      onboardingFingerprint,
      setupAllPendingTasks.map(onboardingTaskStorageId).join(","),
    ].join("::");
    if (setupDrawerAutoOpenedRef.current === fingerprint) return;
    setupDrawerAutoOpenedRef.current = fingerprint;
    setSetupDrawerOpen(true);
  }, [
    canManageSetup,
    loading,
    onboardingFingerprint,
    setupAlertVisible,
    setupAllPendingTasks,
    setupBlocking,
  ]);

  const promptForOnboardingTask = useCallback((task) => {
    if (!task) return;
    const taskId = onboardingTaskStorageId(task);
    const envName = task.required_env || task.required_envs?.[0] || "this API key";
    setOnboardingConversation({ phase: "awaiting_key", activeTaskId: taskId, draftUser: {} });
    appendChatMessage(makeLocalMessage(
      "assistant",
      [
        `Let’s review **${task.provider_label || task.provider || "this service"}**.`,
        "",
        `${envName} unlocks ${task.unlocks?.tools || 0} tool${task.unlocks?.tools === 1 ? "" : "s"}${task.unlocks?.agents ? ` and ${task.unlocks.agents} agent${task.unlocks.agents === 1 ? "" : "s"}` : ""}.`,
        "Paste the key here and I’ll save it securely, or type **skip** to do it later.",
      ].join("\n"),
      {
        metadata: {
          kind: "onboarding_chat",
          step: "key_prompt",
          task_id: taskId,
          provider: task.provider,
        },
      },
    ));
  }, []);

  const offerOnboardingUserSetup = useCallback(() => {
    setOnboardingConversation({ phase: "offer_users", activeTaskId: "", draftUser: {} });
    appendChatMessage(makeLocalMessage(
      "assistant",
      "All setup keys have been reviewed. Would you like to add users now? Reply **yes** to add a user or **no** to finish onboarding.",
      { metadata: { kind: "onboarding_chat", step: "offer_users" } },
    ));
  }, []);

  const completeChatOnboarding = useCallback((content = "Your organization is ready. Casper can now help users run agents, tools, workflows, and knowledge searches.") => {
    setOnboardingConversation({ phase: "complete", activeTaskId: "", draftUser: {} });
    setOnboardingComplete(true);
    persistOnboardingComplete(true);
    appendChatMessage(makeLocalMessage("assistant", content, {
      metadata: { kind: "onboarding_chat", step: "complete" },
    }));
  }, [persistOnboardingComplete]);

  const advanceOnboardingAfterKeyReview = useCallback((reviewedTaskIds) => {
    const remaining = getReviewableOnboardingTasks(onboardingStatus, reviewedTaskIds);
    if (remaining.length) {
      promptForOnboardingTask(remaining[0]);
      return;
    }
    offerOnboardingUserSetup();
  }, [offerOnboardingUserSetup, onboardingStatus, promptForOnboardingTask]);

  const startChatOnboarding = useCallback(() => {
    if (!canManageSetup || !onboardingStatus?.visible || onboardingComplete) return;
    setSetupDrawerOpen(false);
    const reviewable = getReviewableOnboardingTasks(onboardingStatus, onboardingReviewedTaskIds);
    appendChatMessage(makeLocalMessage(
      "assistant",
      "Hello, let’s go through a few steps to get your organization set up.",
      { metadata: { kind: "onboarding_chat", step: "intro" } },
    ));
    if (reviewable.length) {
      appendChatMessage(makeLocalMessage(
        "assistant",
        `I found ${reviewable.length} optional service key${reviewable.length === 1 ? "" : "s"} to review. We’ll go one by one, and you can set each key or skip it for later.`,
        { metadata: { kind: "onboarding_chat", step: "key_intro" } },
      ));
      promptForOnboardingTask(reviewable[0]);
      return;
    }
    offerOnboardingUserSetup();
  }, [
    canManageSetup,
    onboardingComplete,
    onboardingReviewedTaskIds,
    onboardingStatus,
    offerOnboardingUserSetup,
    promptForOnboardingTask,
  ]);

  useEffect(() => {
    if (!canManageSetup || !onboardingStatus?.visible || onboardingComplete) return;
    if (onboardingConversation.phase !== "idle") return;
    if (chatMessages.length > 0) return;
    if (!setupAllPendingTasks.length) return;
    startChatOnboarding();
  }, [
    canManageSetup,
    chatMessages.length,
    onboardingComplete,
    onboardingConversation.phase,
    onboardingStatus?.visible,
    setupAllPendingTasks.length,
    startChatOnboarding,
  ]);

  const currentTaskText = (value = null) => {
    for (const candidate of [value, intent, resolvedIntent]) {
      const text = String(candidate || "").trim();
      if (text) return text;
    }
    return "";
  };

  const clearInlineRunArtifacts = () => {
    setArtifacts([]);
    setArtifactError("");
    setArtifactsLoading(false);
  };

  const createPromptAttachments = (files, responseFiles = []) => files.map((file, index) => {
    const responseFile = responseFiles[index] || {};
    const previewUrl = isImageAttachment({ name: file.name, type: file.type })
      ? URL.createObjectURL(file)
      : null;
    if (previewUrl) {
      uploadPreviewUrlsRef.current.add(previewUrl);
    }
    return {
      id: `${Date.now()}-${index}-${file.name}`,
      name: file.name,
      filename: responseFile.filename || file.name,
      relative_path: responseFile.relative_path || file.webkitRelativePath || file.name,
      type: file.type,
      size: responseFile.size || file.size,
      preview_url: previewUrl,
      uploaded: true,
    };
  });

  const removePendingPromptFile = (attachment) => {
    if (attachment?.preview_url) {
      try {
        URL.revokeObjectURL(attachment.preview_url);
      } catch {
        // Best-effort cleanup.
      }
      uploadPreviewUrlsRef.current.delete(attachment.preview_url);
    }
    setPendingPromptFiles((current) => current.filter((item) => item !== attachment));
  };

  const stopActiveStream = () => {
    if (streamCleanupRef.current) {
      streamCleanupRef.current();
      streamCleanupRef.current = null;
    }
  };

  const refreshSessionMessage = async (sessionId, assistantMessageId) => {
    if (!sessionId) return null;
    const data = await getCasperSession(sessionId);
    setSelectedSession(data);
    persistActiveSessionId(sessionId);
    const serverMessage = (data?.messages || []).find((message) => message.id === assistantMessageId)
      || [...(data?.messages || [])].reverse().find((message) => message.role === "assistant");
    if (serverMessage?.id) {
      const mappedServerMessage = mapServerMessage(serverMessage);
      updateChatMessage(serverMessage.id, {
        content: mappedServerMessage.content,
        status: mappedServerMessage.status,
        run_number: mappedServerMessage.run_number,
        agent_version: mappedServerMessage.agent_version,
        metadata: mappedServerMessage.metadata,
        feedback: mappedServerMessage.feedback,
        feedbackNote: mappedServerMessage.feedbackNote,
        feedbackPending: false,
      });
      if (["completed", "failed"].includes(serverMessage.status || "completed")) {
        setLoading(false);
      }
    }
    return data;
  };

  const attachCasperStream = ({ endpoint, assistantMessageId, sessionId }) => {
    if (!endpoint || !assistantMessageId) return;
    stopActiveStream();
    let finalized = false;
    let safetyTimer = null;
    let stream = null;
    const finalizeStream = async () => {
      if (finalized) return;
      finalized = true;
      if (safetyTimer) {
        clearTimeout(safetyTimer);
        safetyTimer = null;
      }
      try {
        const data = await refreshSessionMessage(sessionId, assistantMessageId);
        refreshSessions();
        await refreshArtifacts(runContextsFromSessionData(data));
      } catch {
        // The answer may already be visible; final refresh should fail open.
      } finally {
        setLoading(false);
        streamCleanupRef.current = null;
      }
    };
    stream = streamAgentRunResult(
      endpoint,
      (content) => {
        updateChatMessage(assistantMessageId, {
          content,
          status: "streaming",
        });
      },
      async () => {
        updateChatMessage(assistantMessageId, {
          status: "failed",
          content: "Casper lost the live stream. Refreshing the final result...",
        });
        await finalizeStream();
      },
      {
        typewriterSpeed: 0,
        onAnimationEnd: finalizeStream,
      },
    );
    safetyTimer = setTimeout(finalizeStream, 120000);
    streamCleanupRef.current = () => {
      if (safetyTimer) {
        clearTimeout(safetyTimer);
        safetyTimer = null;
      }
      if (stream?.cleanup) {
        stream.cleanup();
      }
    };
  };

  const runWithAgent = async (agent = selectedAgent, value = intent, options = {}) => {
    const {
      skipPreview = false,
      executionPreview = null,
      forceDirectAnswer = false,
      promptAttachments: providedPromptAttachments = null,
      promptFileReferences: providedPromptFileReferences = null,
      suppressUserMessage = false,
    } = options;
    const text = value.trim();
    if (!text) return;
    let workflowPollingStarted = false;
    const promptAttachments = Array.isArray(providedPromptAttachments)
      ? providedPromptAttachments
      : pendingPromptFiles;
    const promptFileReferences = Array.isArray(providedPromptFileReferences)
      ? providedPromptFileReferences
      : promptAttachments.map(attachmentPromptReference);
    const reusableSession = forceDirectAnswer && selectedSession?.session?.agent_id === "casper"
      ? selectedSession.session
      : agent && selectedSession?.session?.agent_id === agent.id
        ? selectedSession.session
        : null;
    setLoading(true);
    setError("");
    setExecutionNotice(null);
    setAuthRequired([]);
    setResolution(null);
    setRoutePickerOpen(false);
    clearInlineRunArtifacts();
    setResolvedIntent(text);
    setIntent("");
    setPendingPromptFiles([]);
    if (!suppressUserMessage) {
      appendChatMessage(makeLocalMessage("user", text, {
        attachments: promptAttachments,
        metadata: { attachments: promptAttachments },
      }));
    }

    if (!skipPreview) {
      const previewStatusId = appendChatMessage(makeLocalMessage(
        "system",
        "Designing workflow...",
        { kind: "status" },
      ));
      try {
        const data = await planCasperIntent({
          prompt: text,
          session_id: reusableSession?.id,
          files: promptFileReferences.length ? promptFileReferences : undefined,
          include_resolution: true,
          ask_before_run: true,
        });
        removeChatMessage(previewStatusId);
        if (data.readiness) {
          setOnboardingStatus(data.readiness);
        }
        const preview = data.execution_preview;
        if (!preview) {
          await runWithAgent(agent, text, {
            skipPreview: true,
            promptAttachments,
            promptFileReferences,
            suppressUserMessage: true,
          });
          return;
        }
        if (preview.auto_run_allowed && preview.decision === "auto_run_allowed") {
          const entity = preview.selected_entity || {};
          const agentForRun = entity.type === "agent" ? entity : agent;
          if (entity.type === "agent") {
            setSelectedAgent(entity);
          }
          await runWithAgent(agentForRun, text, {
            skipPreview: true,
            executionPreview: preview,
            promptAttachments,
            promptFileReferences,
            suppressUserMessage: true,
          });
          return;
        }
        if (preview.decision === "no_execution") {
          const isDirectPreview = preview.mode === "direct"
            || preview.execution_type === "direct_answer"
            || data.planner?.execution_type === "direct_answer";
          if (isDirectPreview) {
            await runWithAgent(null, text, {
              skipPreview: true,
              executionPreview: preview,
              forceDirectAnswer: true,
              promptAttachments,
              promptFileReferences,
              suppressUserMessage: true,
            });
            return;
          }
          appendChatMessage(makeLocalMessage("assistant", data.planner?.support_answer
            || data.planner?.user_facing_summary
            || preview.summary
            || "Casper answered this from product context.", {
            status: "completed",
            metadata: {
              planner: data.planner,
              interaction_decision: data.interaction_decision,
              readiness: data.readiness,
              resolution: data.resolution,
              execution_preview: preview,
            },
          }));
          return;
        }
        appendChatMessage(makeLocalMessage("assistant", preview.summary || "I found the best route for this request.", {
          status: "preview",
          metadata: {
            planner: data.planner,
            readiness: data.readiness,
            resolution: data.resolution,
            workflow_plan: data.workflow_plan,
            dag_definition: data.dag_definition,
            validation: data.validation,
            execution_preview: preview,
            interaction_decision: data.interaction_decision,
            prompt_text: text,
            files: promptFileReferences,
            prompt_attachments: promptAttachments,
          },
        }));
      } catch (err) {
        removeChatMessage(previewStatusId);
        setError(err.message || "Casper could not preview that request");
      } finally {
        setLoading(false);
      }
      return;
    }

    const routeStatusCopy = forceDirectAnswer
      ? "Casper is answering directly."
      : executionPreview?.selected_entity
      ? entityRouteCopy(executionPreview.selected_entity)
      : "Casper is starting the run.";
    const routeStatusId = appendChatMessage(makeLocalMessage("system", routeStatusCopy, { kind: "status" }));
    try {
      const data = await runCasperChat({
        message: text,
        agent_id: agent?.id,
        agent_version: agent?.version,
        session_id: reusableSession?.id,
        title: text.slice(0, 96),
        files: promptFileReferences.length ? promptFileReferences : undefined,
        force_direct_answer: forceDirectAnswer || undefined,
        preview_id: executionPreview?.preview_id,
        preview_token: executionPreview?.confirmation_token || executionPreview?.preview_token,
        preview_decision: executionPreview?.decision,
        confirmed_execution_type: executionPreview?.execution_type || executionPreview?.mode,
        confirmed_entity_type: executionPreview?.selected_entity?.type,
        confirmed_entity_id: executionPreview?.selected_entity?.id,
        confirmed_entity_version: executionPreview?.selected_entity?.version,
        confirmed_workflow_plan: executionPreview?.workflow_plan,
      });
      if (data.status === "onboarding_required") {
        removeChatMessage(routeStatusId);
        if (data.readiness) {
          setOnboardingStatus(data.readiness);
        }
        appendOnboardingGuideMessage(data.readiness);
        appendChatMessage(makeLocalMessage(
          "system",
          data.message || "I opened the Casper setup assistant. Finish the required service before this run.",
          { kind: "status" },
        ));
        return;
      }
      if (data.status === "needs_selection") {
        removeChatMessage(routeStatusId);
        appendChatMessage(makeLocalMessage(
          "system",
          data.message || "Casper could not find a kernel-approved route for this request.",
          { kind: "status" },
        ));
        return;
      }
      if (data.status === "completed") {
        removeChatMessage(routeStatusId);
        refreshSessions();
        if (data.session_id || data.session?.id) {
          persistActiveSessionId(data.session_id || data.session.id);
        }
        if (data.session) {
          setSelectedSession({
            session: data.session,
            messages: [data.user_message, data.assistant_message].filter(Boolean),
          });
        }
        const assistantMessage = data.assistant_message;
        if (assistantMessage?.id) {
          appendChatMessage({
            ...mapServerMessage(assistantMessage),
            content: assistantMessage.content || data.message || "",
            status: assistantMessage.status || "completed",
          });
        } else {
          appendChatMessage(makeLocalMessage("assistant", data.message || "Casper answered this from product context.", {
            status: "completed",
            metadata: {
              planner: data.planner,
              readiness: data.readiness,
            },
          }));
        }
        return;
      }
      if (data.status === "workflow_plan") {
        removeChatMessage(routeStatusId);
        setExecutionNotice(data);
        if (!data.validation?.valid) {
          appendChatMessage(makeLocalMessage("assistant", data.message || "I prepared a workflow plan for this.", {
            status: "needs_resolution",
            metadata: {
              planner: data.planner,
              workflow_plan: data.workflow_plan,
              dag_definition: data.dag_definition,
              validation: data.validation,
            },
          }));
          return;
        }
        const workflowResponse = await executeCasperWorkflow({
          prompt: text,
          workflow_plan: data.workflow_plan,
          dag_definition: data.dag_definition,
          session_id: reusableSession?.id,
          files: promptFileReferences.length ? promptFileReferences : undefined,
          persist: false,
        });
        if (workflowResponse?.status === "confirmation_required") {
          appendChatMessage(makeLocalMessage("assistant", workflowResponse.message || "Confirm before Casper starts this workflow.", {
            status: "completed",
            metadata: {
              planner: workflowResponse.planner || data.planner,
              workflow_plan: workflowResponse.workflow_plan || data.workflow_plan,
              dag_definition: data.dag_definition,
              validation: workflowResponse.validation || data.validation,
              confirmation_required: workflowResponse,
            },
          }));
          return;
        }
        const runInfo = workflowRunInfoFromResponse(workflowResponse);
        const workflowMessageId = appendChatMessage(makeLocalMessage(
          "assistant",
          workflowResponse?.planner?.user_facing_summary || data.message || "Casper is coordinating the workflow...",
          {
            status: "streaming",
            metadata: {
              planner: workflowResponse?.planner || data.planner,
              workflow_plan: workflowResponse?.workflow_plan || data.workflow_plan,
              dag_definition: workflowResponse?.dag_definition || data.dag_definition,
              workflow_run: runInfo,
              workflow_status: null,
            },
          },
        ));
        if (runInfo) {
          workflowPollingStarted = true;
          pollWorkflowRun(runInfo, workflowMessageId);
        } else {
          setLoading(false);
        }
        refreshSessions();
        return;
      }
      removeChatMessage(routeStatusId);
      if (data.selected_agent) {
        setSelectedAgent(data.selected_agent);
      }
      refreshSessions();
      if (data.session_id || data.session?.id) {
        persistActiveSessionId(data.session_id || data.session.id);
      }
      if (data.session) {
        setSelectedSession({
          session: data.session,
          messages: [data.user_message, data.assistant_message].filter(Boolean),
        });
      }
      const assistantMessage = data.assistant_message;
      if (assistantMessage?.id) {
        appendChatMessage({
          ...mapServerMessage(assistantMessage),
          content: assistantMessage.content || "",
          status: assistantMessage.status || "pending",
        });
        if (data.casper_events_endpoint) {
          attachCasperStream({
            endpoint: data.casper_events_endpoint,
            assistantMessageId: assistantMessage.id,
            sessionId: data.session_id,
          });
          return;
        }
      }
      if (!assistantMessage?.id) {
        appendChatMessage(makeLocalMessage("assistant", data.message || "Casper started the work.", { status: "pending" }));
      }
      const refreshed = await refreshSessionMessage(data.session_id, assistantMessage?.id);
      await refreshArtifacts(runContextsFromSessionData(refreshed));
    } catch (err) {
      removeChatMessage(routeStatusId);
      setError(err.message || "Casper could not start the run");
    } finally {
      if (!streamCleanupRef.current && !workflowPollingStarted) {
        setLoading(false);
      }
    }
  };

  const runExecutionPreviewMessage = async (message) => {
    const preview = message?.metadata?.execution_preview;
    if (!preview) return;
    const promptText = String(message?.metadata?.prompt_text || promptForAssistantMessage(message) || "").trim();
    if (preview.decision === "blocked" || preview.mode === "blocked") {
      if (preview.setup_action_allowed) {
        setSetupDrawerOpen(true);
        appendChatMessage(makeLocalMessage(
          "system",
          "I opened setup. Finish the missing service, then ask Casper to retry.",
          { kind: "status" },
        ));
      } else {
        await copyTextToClipboard(preview.admin_note || preview.summary || "Please ask an admin to finish Casper setup.");
      }
      return;
    }
    updateChatMessage(message.id, (current) => ({
      status: current.status === "preview" ? "completed" : current.status,
      metadata: {
        ...(current.metadata || {}),
        preview_confirmed: true,
        execution_preview: preview,
      },
    }));
    if (preview.mode === "workflow" && message?.metadata?.workflow_plan) {
      await runWorkflowFromMessage(message, { confirmed: true, preview });
      return;
    }
    const entity = preview.selected_entity || {};
    const entityExecutionRequest = buildPreviewEntityExecutionRequest({
      preview,
      promptText,
      files: message?.metadata?.files || [],
      input: message?.metadata?.run_card_inputs || {},
    });
    if (entityExecutionRequest) {
      setLoading(true);
      setError("");
      setExecutionNotice(null);
      setAuthRequired([]);
      const routeStatusId = appendChatMessage(makeLocalMessage("system", entityRouteCopy(entity), { kind: "status" }));
      try {
        const data = await executeCasperEntity(entityExecutionRequest);
        if (data?.auth_required?.length) setAuthRequired(data.auth_required);
        removeChatMessage(routeStatusId);
        setExecutionNotice(data);
        appendChatMessage(makeLocalMessage(
          "assistant",
          data?.message || `${entity.display_name || entity.name || "The selected resource"} finished.`,
          {
            status: data?.status || "completed",
            metadata: data,
          },
        ));
      } catch (err) {
        removeChatMessage(routeStatusId);
        setError(err.message || "Casper could not execute the selected route");
      } finally {
        setLoading(false);
      }
      return;
    }
    const agentForRun = entity.type === "agent" ? entity : selectedAgent;
    if (entity.type === "agent") {
      setSelectedAgent(entity);
    }
    await runWithAgent(agentForRun, promptText, {
      skipPreview: true,
      executionPreview: preview,
      promptAttachments: message?.metadata?.prompt_attachments || [],
      promptFileReferences: message?.metadata?.files || [],
      suppressUserMessage: true,
    });
  };

  const runExecutionPreviewDirectAnswer = async (message) => {
    const preview = message?.metadata?.execution_preview;
    if (!preview) return;
    const promptText = String(message?.metadata?.prompt_text || promptForAssistantMessage(message) || "").trim();
    updateChatMessage(message.id, (current) => ({
      status: current.status === "preview" ? "completed" : current.status,
      metadata: {
        ...(current.metadata || {}),
        preview_confirmed: true,
        preview_direct_answer: true,
        execution_preview: preview,
      },
    }));
    await runWithAgent(null, promptText, {
      skipPreview: true,
      forceDirectAnswer: true,
      executionPreview: preview,
      promptAttachments: message?.metadata?.prompt_attachments || [],
      promptFileReferences: message?.metadata?.files || [],
      suppressUserMessage: true,
    });
  };

  const changeExecutionPreviewRoute = async (message, picked) => {
    const metadata = message?.metadata || {};
    if (metadata.execution_preview?.preview_id) {
      auditCasperPreviewEvent({
        event: "casper_preview_changed",
        preview_id: metadata.execution_preview.preview_id,
        status: "user_requested_change",
        detail: `mode=${metadata.execution_preview.mode || "unknown"}`,
      }).catch(() => {});
    }

    // --- R2-3b: RunCard alt-pick / Build-fresh → deterministic re-preview ---
    // Honor the picked resource (or "Build fresh") by re-previewing through
    // /casper/plan with forced params — no LLM re-plan, no old route picker.
    const promptText = String(metadata.prompt_text || promptForAssistantMessage(message) || "").trim();
    const sessionId = selectedSession?.session?.id || readActiveSessionId();
    const repreviewArgs = buildRepreviewPlanArgs({ picked, prompt: promptText, sessionId });
    if (repreviewArgs) {
      setLoading(true);
      setError("");
      try {
        const data = await planCasperIntent({
          ...repreviewArgs,
          include_resolution: true,
          ask_before_run: true,
        });
        const preview = data.execution_preview;
        if (!preview) {
          setError("Casper could not re-preview that route.");
          return;
        }
        updateChatMessage(message.id, (current) => ({
          status: "preview",
          metadata: {
            ...(current.metadata || {}),
            planner: data.planner,
            readiness: data.readiness ?? current.metadata?.readiness,
            resolution: data.resolution ?? current.metadata?.resolution,
            workflow_plan: data.workflow_plan,
            dag_definition: data.dag_definition,
            validation: data.validation,
            execution_preview: preview,
            interaction_decision: data.interaction_decision,
            preview_confirmed: false,
          },
        }));
      } catch (err) {
        setError(err.message || "Casper could not re-preview that route.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // --- Legacy non-RunCard route picker (unchanged) ---
    const alternatives = Array.isArray(metadata.execution_preview?.alternatives)
      ? metadata.execution_preview.alternatives
      : [];
    if (alternatives.length > 0) {
      setResolution(resolutionFromRouteAlternatives(alternatives));
      setRoutePickerOpen(true);
      appendChatMessage(makeLocalMessage(
        "system",
        "I surfaced the compatible route alternatives below.",
        { kind: "status" },
      ));
      return;
    }
    setResolution(null);
    setRoutePickerOpen(false);
    appendChatMessage(makeLocalMessage(
      "system",
      "I do not have another compatible ready route for this request.",
      { kind: "status" },
    ));
  };

  const executeEntity = async (entity, value = null) => {
    if (!entity) return;
    setRoutePickerOpen(false);
    const submittedValue = currentTaskText(value);
    if (entity.type === "agent") {
      setSelectedAgent(entity);
      setExecutionNotice(null);
      await runWithAgent(entity, submittedValue);
      return;
    }
    const text = submittedValue || entity.reason || entity.description || entity.name;
    setLoading(true);
    setError("");
    setExecutionNotice(null);
    setAuthRequired([]);
    if (submittedValue) {
      appendChatMessage(makeLocalMessage("user", submittedValue));
      setResolvedIntent(submittedValue);
      setIntent("");
    }
    const routeStatusId = appendChatMessage(makeLocalMessage("system", entityRouteCopy(entity), { kind: "status" }));
    try {
      const data = await executeCasperEntity({
        intent: text,
        entity_type: entity.type,
        entity_id: entity.id,
        entity_version: entity.version,
        action: entity.action,
        input: submittedValue ? { intent: submittedValue } : {},
      });
      if (data?.auth_required?.length) setAuthRequired(data.auth_required);
      removeChatMessage(routeStatusId);
      if (String(data?.next_step || "").startsWith("select")) {
        setExecutionNotice(null);
        return;
      }
      setExecutionNotice(data);
      appendChatMessage(makeLocalMessage(
        "assistant",
        data?.message || `${entity.display_name || entity.name} is ready.`,
        {
          status: data?.status || "completed",
          metadata: data,
        },
      ));
    } catch (err) {
      removeChatMessage(routeStatusId);
      setError(err.message || "Casper could not execute that action");
    } finally {
      setLoading(false);
    }
  };

  const openSession = async (session) => {
    if (!session?.id) return;
    setLoading(true);
    setError("");
    try {
      stopActiveStream();
      const data = await getCasperSession(session.id);
      persistActiveSessionId(session.id);
      setSelectedSession(data);
      setSelectedAgent(agentFromSession(data?.session));
      setResolution(null);
      setRoutePickerOpen(false);
      setExecutionNotice(null);
      clearLocalWorkflowTranscript();
      setChatMessages((data?.messages || []).map(mapServerMessage));
      setSessionsOpen(false);
    } catch (err) {
      setError(err.message || "Could not open that Casper session");
    } finally {
      setLoading(false);
    }
  };

  const restoreSessionById = useCallback(async (sessionId, { reconnect = false } = {}) => {
    if (!sessionId) return null;
    setError("");
    try {
      const data = await getCasperSession(sessionId);
      persistActiveSessionId(sessionId);
      setSelectedSession(data);
      setSelectedAgent(agentFromSession(data?.session));
      setResolution(null);
      setRoutePickerOpen(false);
      setExecutionNotice(null);
      setChatMessages((data?.messages || []).map(mapServerMessage));

      if (reconnect) {
        const pendingAssistant = [...(data?.messages || [])].reverse().find(isUnfinishedAssistantMessage);
        if (pendingAssistant?.id) {
          setLoading(true);
          attachCasperStream({
            endpoint: `/casper/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(pendingAssistant.id)}/events`,
            assistantMessageId: pendingAssistant.id,
            sessionId,
          });
        }
      }
      return data;
    } catch (err) {
      clearActiveSessionId();
      clearLocalWorkflowTranscript();
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearActiveSessionId, clearLocalWorkflowTranscript, persistActiveSessionId]);

  useEffect(() => {
    if (!activeSessionStorageKey) return;
    if (restoredSessionKeyRef.current === activeSessionStorageKey) return;
    restoredSessionKeyRef.current = activeSessionStorageKey;
    const sessionId = readActiveSessionId();
    const localWorkflowTranscript = readLocalWorkflowTranscript();
    if (
      localWorkflowTranscript?.messages?.length
      && localWorkflowTranscript.session_id
      && sessionId
      && String(localWorkflowTranscript.session_id) === String(sessionId)
    ) {
      restoreSessionById(sessionId, { reconnect: true });
      return;
    }
    if (localWorkflowTranscript?.messages?.length && !localWorkflowTranscript.workflow_run) {
      if (hasRestorableCasperLocalTranscript(localWorkflowTranscript, {
        activeSessionId: sessionId,
        appBootId: casperAppBootId,
      })) {
        setSelectedSession(null);
        setSelectedAgent(null);
        setResolution(null);
        setRoutePickerOpen(false);
        setExecutionNotice(null);
        setChatMessages(localWorkflowTranscript.messages);
      } else {
        clearLocalWorkflowTranscript();
      }
      return;
    }
    if (localWorkflowTranscript?.workflow_run && !localWorkflowTranscript.session_id) {
      return;
    }
    if (sessionId) {
      restoreSessionById(sessionId, { reconnect: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionStorageKey, clearLocalWorkflowTranscript, readActiveSessionId, readLocalWorkflowTranscript, restoreSessionById]);

  useEffect(() => {
    if (chatMessages.length > 0 || loading) return;
    const localWorkflowTranscript = readLocalWorkflowTranscript();
    if (localWorkflowTranscript?.messages?.length) {
      const sessionId = readActiveSessionId();
      if (localWorkflowTranscript.session_id && sessionId && String(localWorkflowTranscript.session_id) === String(sessionId)) {
        return;
      }
      if (hasRestorableCasperLocalTranscript(localWorkflowTranscript, {
        activeSessionId: sessionId,
        appBootId: casperAppBootId,
      })) {
        return;
      }
      if (!localWorkflowTranscript.workflow_run) {
        clearLocalWorkflowTranscript();
      }
      if (localWorkflowTranscript.workflow_run && !localWorkflowTranscript.session_id) {
        return;
      }
    }
    const activeSession = sessions.find((session) => session.status === "active" && Number(session.message_count || 0) > 0);
    if (activeSession?.id && activeSession.id !== readActiveSessionId()) {
      restoreSessionById(activeSession.id, { reconnect: true });
    }
  }, [chatMessages.length, clearLocalWorkflowTranscript, loading, readActiveSessionId, readLocalWorkflowTranscript, restoreSessionById, sessions]);

  const sessionRunContexts = useMemo(() => {
    return runContextsFromSessionData(selectedSession);
  }, [selectedSession]);
  const currentRunContext = sessionRunContexts[0] || null;
  const artifactRunContextKey = useMemo(() => (
    sessionRunContexts
      .map((context) => `${context.agentId}:${context.version}:${context.runNumber}`)
      .join("|")
  ), [sessionRunContexts]);

  const refreshArtifacts = async (contextOverride = null) => {
    setArtifactError("");
    setArtifacts([]);
    const runContexts = Array.isArray(contextOverride)
      ? contextOverride
      : contextOverride
        ? [contextOverride]
        : sessionRunContexts;
    if (!runContexts.length) {
      return;
    }
    setArtifactsLoading(true);
    try {
      const artifactGroups = await Promise.all(runContexts.map(async (runContext) => {
        const data = await getAgentRunArtifacts(
          runContext.agentId,
          runContext.version,
          runContext.runNumber,
        );
        return (data?.artifacts || []).map((artifact) => ({
          ...artifact,
          run_context: runContext,
        }));
      }));
      const nextArtifacts = [];
      const seen = new Set();
      artifactGroups.flat().forEach((artifact) => {
        const context = artifact.run_context || {};
        const filename = artifact.filename || artifact.name || "artifact";
        const key = `${context.agentId}:${context.version}:${context.runNumber}:${filename}`;
        if (seen.has(key)) return;
        seen.add(key);
        nextArtifacts.push(artifact);
      });
      setArtifacts(nextArtifacts);
    } catch (err) {
      setArtifactError(err.message || "Could not load artifacts");
    } finally {
      setArtifactsLoading(false);
    }
  };

  useEffect(() => {
    if (!artifactRunContextKey) {
      setArtifacts([]);
      setArtifactError("");
      return;
    }
    refreshArtifacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactRunContextKey]);

  const openArtifacts = async () => {
    setArtifactsOpen(true);
    await refreshArtifacts();
  };

  const downloadArtifact = async (artifact, contextOverride = null) => {
    const runContext = contextOverride || artifact?.run_context || artifact?.runContext || currentRunContext;
    if (!artifact?.filename || !runContext) return;
    try {
      const response = await downloadArtifactFile(
        runContext.agentId,
        runContext.version,
        runContext.runNumber,
        artifact.filename,
      );
      if (!response?.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = artifact.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setArtifactError(err.message || "Download failed");
      setArtifactsOpen(true);
    }
  };

  const copyTextToClipboard = async (value) => {
    const text = String(value || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.setAttribute("readonly", "readonly");
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      textArea.remove();
    }
  };

  const shareCasperText = async ({ title = "Next Moca Casper", text = "" } = {}) => {
    const shareText = String(text || "").trim();
    const shareUrl = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: shareText || title,
          url: shareUrl,
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") {
          return;
        }
      }
    }
    await copyTextToClipboard(`${shareText}${shareText ? "\n" : ""}${shareUrl}`);
  };

  const movePromptToComposer = (message) => {
    const text = String(message?.content || "");
    setIntent(text);
    setResolvedIntent(text);
    setTimeout(() => inputRef.current?.focus?.(), 0);
  };

  const promptForAssistantMessage = (message) => {
    const messageIndex = chatMessages.findIndex((item) => item.id === message.id);
    for (let index = messageIndex - 1; index >= 0; index -= 1) {
      if (chatMessages[index]?.role === "user") {
        return chatMessages[index].content || "";
      }
    }
    const title = selectedSession?.session?.title || "";
    return String(title).replace(/^Casper:\s*/i, "") || resolvedIntent || intent || "";
  };

  const feedbackArtifactsForMessage = (message) => {
    const runNumber = Number(message?.run_number || currentRunContext?.runNumber || 0);
    return (artifacts || [])
      .filter((artifact) => {
        const artifactRun = Number(artifact?.run_context?.runNumber || artifact?.run_number || runNumber || 0);
        return !runNumber || artifactRun === runNumber;
      })
      .map((artifact) => ({
        name: artifact.name || artifact.filename,
        filename: artifact.filename,
        type: artifact.type,
        run_number: artifact?.run_context?.runNumber || artifact?.run_number || message?.run_number,
        agent_version: artifact?.run_context?.version || artifact?.agent_version || message?.agent_version,
      }));
  };

  const submitOutcomeSignal = async (message, feedback, note = "") => {
    const sessionId = selectedSession?.session?.id || readActiveSessionId();
    const messageId = message?.id;
    const messageKey = String(messageId || "");
    const workflowContext = message?.metadata?.workflow_plan || message?.metadata?.workflow_run;
    if ((!workflowContext && (!sessionId || !messageId || messageKey.startsWith("local-"))) || message?.feedbackPending) {
      return;
    }
    const previousFeedback = message.feedback || null;
    const previousNote = message.feedbackNote || null;
    updateChatMessage(messageId, {
      feedback,
      feedbackNote: note || feedback,
      feedbackPending: true,
    });
    try {
      const response = await submitCasperFeedback({
        session_id: workflowContext ? undefined : sessionId,
        message_id: workflowContext ? undefined : messageId,
        feedback,
        prompt: promptForAssistantMessage(message),
        response: message.content || "",
        artifacts: feedbackArtifactsForMessage(message),
        note,
        workflow_plan: message?.metadata?.workflow_plan,
        workflow_id: message?.metadata?.workflow_run?.workflow_id,
        workflow_version: message?.metadata?.workflow_run?.version,
        workflow_run_number: message?.metadata?.workflow_run?.run_number,
      });
      const outcomeFeedback = response?.message?.metadata?.outcome_feedback || {};
      updateChatMessage(messageId, (current) => ({
        feedback: outcomeFeedback.feedback || feedback,
        feedbackNote: outcomeFeedback.note || note || feedback,
        feedbackPending: false,
        metadata: response?.suggest_save_workflow
          ? {
            ...(current.metadata || {}),
            save_suggestion: true,
            workflow_plan: response.workflow_plan || current?.metadata?.workflow_plan || message?.metadata?.workflow_plan,
          }
          : current.metadata,
      }));
      refreshSessions();
    } catch (err) {
      updateChatMessage(messageId, {
        feedback: previousFeedback,
        feedbackNote: previousNote,
        feedbackPending: false,
      });
      setError(err.message || "Could not save outcome signal");
    }
  };

  const workflowRunInfoFromResponse = (response) => {
    const run = response?.run || response?.execution || {};
    const workflowId = run.workflow_id || response?.workflow_id;
    const version = run.version || response?.version || response?.workflow_version || 1;
    const runNumber = run.run_number || response?.run_number;
    if (!workflowId || !version || !runNumber) return null;
    return {
      workflow_id: workflowId,
      version,
      run_number: runNumber,
      status_endpoint: run.status_endpoint,
      logs_endpoint: run.logs_endpoint,
      result_endpoint: run.result_endpoint,
    };
  };

  const workflowOutputToText = (output) => {
    if (output === null || output === undefined || output === "") return "";
    if (typeof output === "string") return output;
    if (Array.isArray(output)) {
      return output.map((item) => workflowOutputToText(item)).filter(Boolean).join("\n\n");
    }
    if (typeof output === "object") {
      if (typeof output.text === "string") return output.text;
      if (typeof output.content === "string") return output.content;
      if (typeof output.message === "string") return output.message;
      if (typeof output.output === "string") return output.output;
      if (typeof output.response === "string") return output.response;
      if (typeof output.final === "string") return output.final;
      const stringValues = Object.values(output).filter((value) => (
        typeof value === "string" && value.trim()
      ));
      if (stringValues.length === 1) return stringValues[0];
      return JSON.stringify(output, null, 2);
    }
    return String(output);
  };

  const workflowApprovalDecisionText = (value) => {
    const raw = value?.raw && typeof value.raw === "object" ? value.raw : value;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return "";
    const decision = raw.decision || raw.approval_decision || raw.status;
    const feedback = raw.feedback || raw.approval_feedback || raw.comment || raw.comments;
    if (!decision && !feedback) return "";
    const lines = [];
    if (decision) {
      const label = String(decision).replace(/_/g, " ");
      lines.push(`**Decision:** ${label.charAt(0).toUpperCase()}${label.slice(1)}`);
    }
    if (feedback) lines.push(`**Feedback:** ${workflowOutputToText(feedback)}`);
    return lines.join("\n\n");
  };

  const workflowStepInputText = (value) => {
    if (!value) return "";
    if (value?.text && String(value.text).trim() && !String(value.text).trim().startsWith("{")) {
      return workflowDisplayDataToText(value.text);
    }
    const raw = value?.raw && typeof value.raw === "object" ? value.raw : value;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      return workflowDisplayDataToText(value?.text || value?.raw || value);
    }

    const sections = [];
    const addSection = (title, sectionValue) => {
      const text = workflowDisplayDataToText(sectionValue);
      if (text.trim()) sections.push(`**${title}**\n${text.trim()}`);
    };

    addSection("Request", raw.request || raw.user_prompt || raw.prompt || raw.intent);
    addSection("Review context", raw.review_output || raw.review || raw.review_notes);

    const previousOutput = raw.previous_output || raw.previous || raw.prior_output;
    const previousDecisionOnly = workflowApprovalDecisionText(previousOutput);
    if (previousOutput && !previousDecisionOnly) {
      addSection("Previous output", previousOutput);
    }

    if (Array.isArray(raw.uploaded_assets) && raw.uploaded_assets.length) {
      const assets = raw.uploaded_assets
        .map((asset) => asset?.name || asset?.filename || asset?.relative_path || asset?.path)
        .filter(Boolean)
        .map((name) => `- ${name}`)
        .join("\n");
      addSection("Uploaded assets", assets);
    }

    const knownKeys = new Set([
      "request",
      "user_prompt",
      "prompt",
      "intent",
      "review_output",
      "review",
      "review_notes",
      "previous_output",
      "previous",
      "prior_output",
      "uploaded_assets",
      "approval_decision",
      "approval_feedback",
    ]);
    const remaining = Object.entries(raw)
      .filter(([key, sectionValue]) => !knownKeys.has(key) && sectionValue !== null && sectionValue !== undefined && sectionValue !== "")
      .map(([key, sectionValue]) => {
        const text = workflowDisplayDataToText(sectionValue);
        return text.trim() ? `**${key.replace(/_/g, " ")}**\n${text.trim()}` : "";
      })
      .filter(Boolean);
    sections.push(...remaining);

    return sections.join("\n\n");
  };

  const workflowDisplayDataToText = (value) => {
    const text = workflowOutputToText(value);
    if (text) return text;
    if (Array.isArray(value)) {
      return value.map((item) => workflowDisplayDataToText(item)).filter(Boolean).join("\n\n");
    }
    return "";
  };

  const workflowStepOutputText = (stepOutput) => (
    workflowDisplayDataToText(stepOutput?.text)
    || workflowDisplayDataToText(stepOutput?.output)
    || workflowDisplayDataToText(stepOutput)
  );

  const workflowTimelineValueText = (value) => {
    if (!value) return "";
    return workflowDisplayDataToText(value?.text || value?.raw || value);
  };

  const workflowTimelineOutputText = (step) => {
    if (step?.is_approval) {
      return (
        workflowApprovalDecisionText(step?.output)
        || workflowApprovalDecisionText(step?.approval?.response_data)
        || workflowTimelineValueText(step?.output)
      );
    }
    return workflowTimelineValueText(step?.output);
  };

  const workflowStepStatusLabel = (status) => {
    const normalized = String(status || "pending").replace(/_/g, " ");
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const workflowStepStatusSx = (status) => {
    const normalized = String(status || "").toLowerCase();
    if (["completed", "success"].includes(normalized)) {
      return {
        borderColor: "rgba(67,120,72,0.30)",
        color: "#437848",
        bgcolor: "rgba(67,120,72,0.08)",
      };
    }
    if (["running", "started", "streaming", "in_progress"].includes(normalized)) {
      return {
        borderColor: "rgba(184,115,51,0.36)",
        color: copperDark,
        bgcolor: "rgba(184,115,51,0.08)",
      };
    }
    if (["waiting_for_approval", "paused"].includes(normalized)) {
      return {
        borderColor: "rgba(225,126,54,0.42)",
        color: "#A45C18",
        bgcolor: "rgba(255,248,238,0.88)",
      };
    }
    if (["failed", "error", "rejected"].includes(normalized)) {
      return {
        borderColor: "rgba(180,60,48,0.28)",
        color: "#9E2F24",
        bgcolor: "rgba(255,241,239,0.82)",
      };
    }
    return {
      borderColor: "rgba(126,116,105,0.18)",
      color: "text.secondary",
      bgcolor: "rgba(246,240,235,0.46)",
    };
  };

  const isUserFacingWorkflowStepOutput = (stepOutput, reviewOutputText = "") => {
    if (!stepOutput) return false;
    const type = String(stepOutput.type || "").toLowerCase();
    const stepId = String(stepOutput.step_id || "").toLowerCase();
    const label = String(stepOutput.label || "").toLowerCase();
    if (type === "human_input") return false;
    if (stepId === "approval" || stepId.includes("approval")) return false;
    if (label.includes("review and approve")) return false;
    if (reviewOutputText && stepOutput.is_review) return false;
    return Boolean(workflowStepOutputText(stepOutput));
  };

  const stopWorkflowPoller = (messageId) => {
    const existing = workflowPollersRef.current?.[messageId];
    if (existing) {
      clearInterval(existing);
      delete workflowPollersRef.current[messageId];
    }
  };

  const pollWorkflowRun = (runInfo, messageId) => {
    if (!runInfo?.workflow_id || !runInfo?.version || !runInfo?.run_number || !messageId) return;
    stopWorkflowPoller(messageId);

    const pollOnce = async () => {
      try {
        const status = await getCasperWorkflowRunStatus(
          runInfo.workflow_id,
          runInfo.version,
          runInfo.run_number,
        );
        const progress = status?.progress || {};
        const currentLabel = progress?.current_step?.label;
        const finalText = workflowOutputToText(status?.final_output);
        const stepOutputs = Array.isArray(status?.step_outputs) ? status.step_outputs : [];
        const reviewText = workflowDisplayDataToText(status?.review_output?.text || status?.review_output);
        const visibleStepOutputs = stepOutputs.filter((stepOutput) => (
          isUserFacingWorkflowStepOutput(stepOutput, reviewText)
        ));
        const latestStepText = workflowStepOutputText([...visibleStepOutputs].reverse().find((stepOutput) => workflowStepOutputText(stepOutput)));
        const nextStatus = status?.status === "success"
          ? "completed"
          : status?.status === "failed"
            ? "failed"
            : status?.workflow_status === "paused"
              ? "paused"
              : "streaming";
        const hasWorkflowDetails = Boolean(
          finalText || reviewText || latestStepText || status?.step_timeline?.length,
        );
        const nextContent = nextStatus === "completed"
          ? hasWorkflowDetails
            ? "Workflow complete. Review the collapsed steps below."
            : "Workflow complete."
          : nextStatus === "failed"
            ? "Workflow failed. Review the step details below."
            : nextStatus === "paused"
              ? "Review the workflow output below, then approve or request changes."
              : "Casper is coordinating the workflow...";
        updateChatMessage(messageId, (current) => ({
          content: nextContent,
          status: nextStatus,
          metadata: {
            ...(current.metadata || {}),
            workflow_status: status,
            workflow_run: runInfo,
            workflow_plan: status?.workflow_plan || current.metadata?.workflow_plan,
            save_suggestion: current.metadata?.save_suggestion || Boolean(status?.save_suggestion && status?.status === "success"),
          },
        }));
        if (currentLabel && nextStatus === "streaming") {
          setExecutionNotice({
            status: "workflow_running",
            message: currentLabel,
            progress,
          });
        }
        if (["completed", "failed", "paused"].includes(nextStatus)) {
          stopWorkflowPoller(messageId);
          setLoading(false);
          if (nextStatus !== "paused") {
            setExecutionNotice(null);
          }
          refreshSessions();
        }
      } catch (err) {
        stopWorkflowPoller(messageId);
        setLoading(false);
        updateChatMessage(messageId, {
          status: "failed",
          content: err.message || "Casper could not refresh workflow progress.",
        });
      }
    };

    pollOnce();
    workflowPollersRef.current[messageId] = setInterval(pollOnce, 1800);
  };

  useEffect(() => {
    if (chatMessages.length > 0 || loading) return;
    const localWorkflowTranscript = readLocalWorkflowTranscript();
    if (
      localWorkflowTranscript?.messages?.length
      && !hasRestorableCasperLocalTranscript(localWorkflowTranscript, {
        activeSessionId: readActiveSessionId(),
        appBootId: casperAppBootId,
      })
    ) {
      clearLocalWorkflowTranscript();
      return;
    }
    const runInfo = localWorkflowTranscript?.workflow_run
      || latestWorkflowRunInfoFromMessages(localWorkflowTranscript?.messages || []);
    if (!runInfo?.workflow_id || !runInfo?.version || !runInfo?.run_number) return;

    let cancelled = false;
    const restoreWorkflowRun = async () => {
      try {
        const status = await getCasperWorkflowRunStatus(
          runInfo.workflow_id,
          runInfo.version,
          runInfo.run_number,
        );
        if (cancelled) return;
        const storedMessages = (localWorkflowTranscript?.messages || []).map((message) => {
          if (!message?.metadata?.workflow_run) return message;
          return {
            ...message,
            status: status?.status === "success"
              ? "completed"
              : status?.status === "failed"
                ? "failed"
                : status?.workflow_status === "paused"
                  ? "paused"
                  : message.status || "streaming",
            content: status?.status === "success"
              ? "Workflow complete. Review the collapsed steps below."
              : status?.workflow_status === "paused"
                ? "Review the workflow output below, then approve or request changes."
                : message.content || "Casper is coordinating the workflow...",
            metadata: {
              ...(message.metadata || {}),
              workflow_run: runInfo,
              workflow_status: status,
              workflow_plan: status?.workflow_plan || message.metadata?.workflow_plan,
              save_suggestion: message.metadata?.save_suggestion || Boolean(status?.save_suggestion && status?.status === "success"),
            },
          };
        });
        const workflowMessage = [...storedMessages].reverse().find((message) => message?.metadata?.workflow_run);
        setSelectedSession(null);
        setSelectedAgent(null);
        setResolution(null);
        setRoutePickerOpen(false);
        setExecutionNotice(null);
        setChatMessages(storedMessages);
        if (workflowMessage?.id && !isWorkflowTerminalStatus(status) && status?.workflow_status !== "paused") {
          setLoading(true);
          pollWorkflowRun(runInfo, workflowMessage.id);
        }
      } catch {
        if (!cancelled) {
          clearLocalWorkflowTranscript();
        }
      }
    };

    restoreWorkflowRun();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessages.length, loading, readActiveSessionId, readLocalWorkflowTranscript, clearLocalWorkflowTranscript]);

  const saveWorkflowFromMessage = async (message) => {
    const workflowPlan = message?.metadata?.workflow_plan;
    if (!workflowPlan) return;
    setLoading(true);
    setError("");
    try {
      const response = await saveCasperWorkflow({
        name: workflowPlan.name,
        description: workflowPlan.user_facing_summary,
        workflow_plan: workflowPlan,
        dag_definition: message?.metadata?.dag_definition,
        visibility: "private",
        status: "draft",
      });
      appendChatMessage(makeLocalMessage(
        "system",
        `Saved workflow: ${response?.name || workflowPlan.name || "Casper workflow"}.`,
        { kind: "status" },
      ));
      if (message?.id) {
        updateChatMessage(message.id, (current) => ({
          metadata: {
            ...(current.metadata || {}),
            workflow_saved: true,
            save_suggestion: false,
            save_suggestion_dismissed: true,
            saved_workflow: response,
          },
        }));
      }
      refreshSessions();
    } catch (err) {
      setError(err.message || "Could not save this workflow");
    } finally {
      setLoading(false);
    }
  };

  const dismissWorkflowSaveSuggestion = (message) => {
    if (!message?.id) return;
    updateChatMessage(message.id, (current) => ({
      metadata: {
        ...(current.metadata || {}),
        save_suggestion: false,
        save_suggestion_dismissed: true,
      },
    }));
  };

  const runWorkflowFromMessage = async (message, options = {}) => {
    const workflowPlan = message?.metadata?.workflow_plan;
    const dagDefinition = message?.metadata?.dag_definition;
    if (!workflowPlan) return;
    const sessionId = selectedSession?.session?.id || readActiveSessionId();
    setLoading(true);
    setError("");
    clearInlineRunArtifacts();
    try {
      const response = await executeCasperWorkflow({
        prompt: message?.metadata?.prompt_text
          || promptForAssistantMessage(message)
          || message?.metadata?.planner?.intent_summary
          || workflowPlan.name,
        workflow_plan: workflowPlan,
        dag_definition: dagDefinition,
        session_id: sessionId || undefined,
        files: Array.isArray(message?.metadata?.files) && message.metadata.files.length
          ? message.metadata.files
          : undefined,
        persist: false,
        confirmed: Boolean(options.confirmed),
        preview_id: options.preview?.preview_id || message?.metadata?.execution_preview?.preview_id,
        preview_token: options.preview?.confirmation_token
          || options.preview?.preview_token
          || message?.metadata?.execution_preview?.confirmation_token
          || message?.metadata?.execution_preview?.preview_token,
        preview_decision: options.preview?.decision || message?.metadata?.execution_preview?.decision,
        confirmed_execution_type:
          options.preview?.execution_type || message?.metadata?.execution_preview?.execution_type || "new_agentic_workflow",
        confirmed_workflow_plan: workflowPlan,
        confirmed_dag_definition: dagDefinition,
      });
      if (response?.status === "confirmation_required") {
        updateChatMessage(message.id, (current) => ({
          metadata: {
            ...(current.metadata || {}),
            confirmation_required: response,
            planner: response.planner || current.metadata?.planner,
            workflow_plan: response.workflow_plan || workflowPlan,
            dag_definition: response.dag_definition || dagDefinition || current.metadata?.dag_definition,
            validation: response.validation || current.metadata?.validation,
          },
        }));
        setLoading(false);
        return;
      }
      const runInfo = workflowRunInfoFromResponse(response);
      const workflowMessageId = appendChatMessage(makeLocalMessage(
        "assistant",
        "Casper is coordinating the workflow...",
        {
          status: "streaming",
          metadata: {
            planner: response?.planner || message?.metadata?.planner,
            workflow_plan: response?.workflow_plan || workflowPlan,
            dag_definition: response?.dag_definition || dagDefinition,
            execution_preview: options.preview || message?.metadata?.execution_preview,
            workflow_run: runInfo,
            workflow_status: null,
          },
        },
      ));
      if (runInfo) {
        pollWorkflowRun(runInfo, workflowMessageId);
      } else {
        setLoading(false);
      }
      refreshSessions();
    } catch (err) {
      setError(err.message || "Could not start this workflow");
      setLoading(false);
    }
  };

  const uploadFilesToCasper = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) {
      return;
    }
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file, file.name);
      formData.append("paths", file.webkitRelativePath || file.name);
    });
    const sessionId = selectedSession?.session?.id;
    const uploadIntent = intent.trim() || "Summarize uploaded files";
    if (!sessionId) {
      if (selectedAgent?.id) {
        formData.append("agent_id", selectedAgent.id);
        if (selectedAgent.version !== undefined && selectedAgent.version !== null) {
          formData.append("agent_version", String(selectedAgent.version));
        }
      }
      formData.append("intent", uploadIntent);
      formData.append("title", `Casper uploads: ${uploadIntent.slice(0, 80)}`);
    }
    setUploading(true);
    setUploadNotice("");
    try {
      const data = sessionId
        ? await uploadCasperSessionFiles(sessionId, formData)
        : await uploadCasperFiles(formData);
      if (data.session) {
        persistActiveSessionId(data.session.id);
        setSelectedSession({ session: data.session, messages: [] });
      }
      if (data.agent) {
        setSelectedAgent(data.agent);
      }
      const promptAttachments = createPromptAttachments(files, Array.isArray(data.files) ? data.files : []);
      setPendingPromptFiles((current) => [...current, ...promptAttachments]);
      const uploadedCount = data.file_count || files.length;
      setUploadNotice(`Attached ${uploadedCount} file${uploadedCount === 1 ? "" : "s"} to your next prompt.`);
      refreshSessions();
    } catch (err) {
      setUploadNotice(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleUploadFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    await uploadFilesToCasper(files);
  };

  const hasDraggedFiles = (event) => {
    const types = Array.from(event.dataTransfer?.types || []);
    if (types.includes("Files")) return true;
    return Array.from(event.dataTransfer?.items || []).some((item) => item.kind === "file");
  };

  const handleChatDragEnter = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setIsDraggingFiles(true);
  };

  const handleChatDragOver = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    setIsDraggingFiles(true);
  };

  const handleChatDragLeave = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDraggingFiles(false);
    }
  };

  const handleChatDrop = async (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
    await uploadFilesToCasper(event.dataTransfer?.files);
  };

  const handleOnboardingConversationReply = async (text) => {
    const phase = onboardingConversation.phase;
    if (!["awaiting_key", "offer_users", "user_email", "user_name", "user_role", "user_password", "user_confirm"].includes(phase)) {
      return false;
    }

    const normalized = text.toLowerCase().trim();
    const activeTask = setupAllPendingTasks.find(
      (task) => onboardingTaskStorageId(task) === onboardingConversation.activeTaskId,
    ) || setupReviewableTasks[0];

    if (phase === "awaiting_key") {
      const reply = interpretOnboardingKeyReply(text);
      if (reply.action === "empty") return true;
      if (reply.action === "skip") {
        appendChatMessage(makeLocalMessage("user", "Skip for now", {
          metadata: { kind: "onboarding_chat", action: "skip_key" },
        }));
        skipOnboardingTask(activeTask);
        const reviewed = rememberReviewedOnboardingTask(activeTask);
        appendChatMessage(makeLocalMessage(
          "assistant",
          `${activeTask?.provider_label || "That service"} is skipped for now. I’ll keep it in the setup alert so an admin can finish it later.`,
          { metadata: { kind: "onboarding_chat", step: "key_skipped" } },
        ));
        advanceOnboardingAfterKeyReview(reviewed);
        return true;
      }

      appendChatMessage(makeLocalMessage("user", "•••••••• (key hidden)", {
        metadata: { kind: "onboarding_chat", action: "save_key_redacted" },
      }));
      setLoading(true);
      setError("");
      try {
        await saveOnboardingCredential(activeTask, reply.secret);
        const reviewed = rememberReviewedOnboardingTask(activeTask);
        appendChatMessage(makeLocalMessage(
          "assistant",
          `${activeTask?.provider_label || "That service"} is connected. I saved the key in Key Manager and refreshed readiness.`,
          { metadata: { kind: "onboarding_chat", step: "key_saved" } },
        ));
        advanceOnboardingAfterKeyReview(reviewed);
      } catch (err) {
        appendChatMessage(makeLocalMessage(
          "assistant",
          err?.message || "I could not save that key. Paste it again, or type **skip** to continue.",
          { metadata: { kind: "onboarding_chat", step: "key_save_failed" } },
        ));
      } finally {
        setLoading(false);
      }
      return true;
    }

    if (phase === "offer_users") {
      appendChatMessage(makeLocalMessage("user", text));
      if (["yes", "y", "sure", "ok", "okay", "add user", "add users"].includes(normalized)) {
        setOnboardingConversation({ phase: "user_email", activeTaskId: "", draftUser: {} });
        appendChatMessage(makeLocalMessage(
          "assistant",
          "Great. What email address should the first user use?",
          { metadata: { kind: "onboarding_chat", step: "user_email" } },
        ));
        return true;
      }
      completeChatOnboarding("Perfect. Your organization is ready. Skipped services will stay available in the setup alert and Config page for later.");
      return true;
    }

    if (phase === "user_email") {
      appendChatMessage(makeLocalMessage("user", text));
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(text)) {
        appendChatMessage(makeLocalMessage("assistant", "That does not look like a valid email. Please send the user’s email address."));
        return true;
      }
      setOnboardingConversation({
        phase: "user_name",
        activeTaskId: "",
        draftUser: { email: text.trim(), username: text.trim().split("@")[0] },
      });
      appendChatMessage(makeLocalMessage("assistant", "What username or display name should I use for this user?"));
      return true;
    }

    if (phase === "user_name") {
      appendChatMessage(makeLocalMessage("user", text));
      setOnboardingConversation((current) => ({
        phase: "user_role",
        activeTaskId: "",
        draftUser: { ...current.draftUser, username: text.trim() },
      }));
      appendChatMessage(makeLocalMessage(
        "assistant",
        "Which role should this user have? Choose **user**, **developer**, **admin**, or **operator-admin**.",
      ));
      return true;
    }

    if (phase === "user_role") {
      appendChatMessage(makeLocalMessage("user", text));
      const role = normalized.replace("_", "-");
      const allowedRoles = new Set(["user", "developer", "admin", "operator-admin"]);
      if (!allowedRoles.has(role)) {
        appendChatMessage(makeLocalMessage("assistant", "Please choose one of: **user**, **developer**, **admin**, or **operator-admin**."));
        return true;
      }
      setOnboardingConversation((current) => ({
        phase: "user_password",
        activeTaskId: "",
        draftUser: { ...current.draftUser, role },
      }));
      appendChatMessage(makeLocalMessage(
        "assistant",
        "Send a temporary password for this user. I’ll hide it in the chat transcript.",
      ));
      return true;
    }

    if (phase === "user_password") {
      appendChatMessage(makeLocalMessage("user", "•••••••• (password hidden)", {
        metadata: { kind: "onboarding_chat", action: "password_redacted" },
      }));
      setOnboardingConversation((current) => ({
        phase: "user_confirm",
        activeTaskId: "",
        draftUser: { ...current.draftUser, password: text },
      }));
      appendChatMessage(makeLocalMessage(
        "assistant",
        "Ready to create this user. Reply **create** to confirm, or **cancel** to skip user creation.",
      ));
      return true;
    }

    if (phase === "user_confirm") {
      appendChatMessage(makeLocalMessage("user", text));
      if (["cancel", "skip", "no", "not now"].includes(normalized)) {
        completeChatOnboarding("No problem. Your organization is ready. You can add users later from User Management.");
        return true;
      }
      if (!["create", "confirm", "yes", "y"].includes(normalized)) {
        appendChatMessage(makeLocalMessage("assistant", "Reply **create** to add the user, or **cancel** to skip this step."));
        return true;
      }
      setLoading(true);
      setError("");
      try {
        const draft = onboardingConversation.draftUser || {};
        await registerUser({
          username: draft.username,
          email: draft.email,
          password: draft.password,
          role: draft.role || "user",
        });
        appendChatMessage(makeLocalMessage(
          "assistant",
          `User **${draft.username}** has been added. Would you like to add another user? Reply **yes** or **no**.`,
        ));
        setOnboardingConversation({ phase: "offer_users", activeTaskId: "", draftUser: {} });
      } catch (err) {
        appendChatMessage(makeLocalMessage(
          "assistant",
          err?.message || "I could not create that user. Reply **create** to try again, or **cancel** to finish onboarding.",
        ));
      } finally {
        setLoading(false);
      }
      return true;
    }

    return false;
  };

  const connectOAuth = async (signal) => {
    if (!signal?.provider) return;
    setLoading(true);
    setError("");
    try {
      const response = await startOAuthConnect({
        provider: signal.provider,
        required_env: signal.required_env,
        tool_name: signal.tool_name,
        scopes: Array.isArray(signal.scopes) ? signal.scopes : undefined,
        return_to: window.location.href,
      });
      if (response?.authorization_url) {
        window.open(response.authorization_url, "nextmoca_casper_oauth", "popup,width=720,height=760");
      }
    } catch (err) {
      setError(err.message || "Could not start OAuth connection");
    } finally {
      setLoading(false);
    }
  };

  const submitCasperText = async (value) => {
    const text = String(value || "").trim();
    if (!text || loading) return;

    // Control keywords bypass Run Card routing entirely
    if (isCasperControlKeyword(text)) {
      setIntent("");
      setResolvedIntent(text);
      appendChatMessage(makeLocalMessage("user", text));
      appendChatMessage(makeLocalMessage(
        "system",
        `Got it — "${text.toLowerCase()}".`,
        { kind: "control", controlAction: text.toLowerCase() },
      ));
      return;
    }

    const handledOnboardingReply = await handleOnboardingConversationReply(text);
    if (handledOnboardingReply) {
      setIntent("");
      setResolvedIntent(text);
      return;
    }
    if (isCasperSetupPrompt(text) && onboardingStatus?.visible) {
      setIntent("");
      setResolvedIntent(text);
      appendChatMessage(makeLocalMessage("user", text));
      if (!onboardingComplete) {
        startChatOnboarding();
        return;
      }
      setSetupDrawerOpen(true);
      appendChatMessage(makeLocalMessage(
        "system",
        "I opened the Casper setup assistant so we can finish this securely.",
        { kind: "status" },
      ));
      return;
    }
    await runWithAgent(selectedAgent, text);
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    await submitCasperText(intent);
  };

  const renderPromptActions = (message) => (
    <Stack
      direction="row"
      spacing={0.6}
      alignItems="center"
      justifyContent="flex-end"
      sx={{ mt: 0.7, pr: 0.3 }}
    >
      <Tooltip title="Copy prompt">
        <span>
          <IconButton
            size="small"
            sx={messageActionButtonSx()}
            onClick={() => copyTextToClipboard(message.content)}
            disabled={!String(message.content || "").trim()}
          >
            <ContentCopyIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Share prompt">
        <span>
          <IconButton
            size="small"
            sx={messageActionButtonSx()}
            onClick={() => shareCasperText({ title: "Casper prompt", text: message.content })}
            disabled={!String(message.content || "").trim()}
          >
            <ShareIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Edit prompt">
        <span>
          <IconButton
            size="small"
            sx={messageActionButtonSx()}
            onClick={() => movePromptToComposer(message)}
            disabled={loading || !String(message.content || "").trim()}
          >
            <EditOutlinedIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Run again">
        <span>
          <IconButton
            size="small"
            sx={messageActionButtonSx()}
            onClick={() => submitCasperText(message.content)}
            disabled={loading || !String(message.content || "").trim()}
          >
            <PlayArrowIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  const renderResponseActions = (message) => {
    if (message.role !== "assistant" || message.kind !== "message" || message.status !== "completed") {
      return null;
    }
    const messageKey = String(message.id || "");
    const workflowContext = message?.metadata?.workflow_plan || message?.metadata?.workflow_run;
    const feedbackDisabled = (!workflowContext && (!selectedSession?.session?.id || messageKey.startsWith("local-"))) || message.feedbackPending;
    const positiveActive = message.feedback === "up";
    const negativeActive = message.feedback === "down";
    return (
      <Stack
        direction="row"
        spacing={0.6}
        alignItems="center"
        sx={{ mt: 0.7, pl: 0.3 }}
      >
        <Tooltip title="Copy response">
          <span>
            <IconButton
              size="small"
              sx={messageActionButtonSx()}
              onClick={() => copyTextToClipboard(message.content)}
              disabled={!String(message.content || "").trim()}
            >
              <ContentCopyIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Share response">
          <span>
            <IconButton
              size="small"
              sx={messageActionButtonSx()}
              onClick={() => shareCasperText({ title: "Casper response", text: message.content })}
              disabled={!String(message.content || "").trim()}
            >
              <ShareIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Favorite result">
          <span>
            <IconButton
              size="small"
              sx={messageActionButtonSx(positiveActive)}
              onClick={() => submitOutcomeSignal(message, "up", "favorite_result")}
              disabled={feedbackDisabled}
            >
              {positiveActive ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Good outcome">
          <span>
            <IconButton
              size="small"
              sx={messageActionButtonSx(positiveActive)}
              onClick={() => submitOutcomeSignal(message, "up", "positive_outcome")}
              disabled={feedbackDisabled}
            >
              {positiveActive ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Poor outcome">
          <span>
            <IconButton
              size="small"
              sx={messageActionButtonSx(negativeActive)}
              onClick={() => submitOutcomeSignal(message, "down", "negative_outcome")}
              disabled={feedbackDisabled}
            >
              {negativeActive ? <ThumbDownIcon /> : <ThumbDownOutlinedIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.3 }}>
          Outcome signal
        </Typography>
      </Stack>
    );
  };

  const respondToWorkflowApproval = async (approval, message, decision, feedback = "") => {
    if (!approval?.id) return;
    const cleanFeedback = String(feedback || "").trim();
    if (decision === "request_changes" && !cleanFeedback) {
      setError("Tell Casper what to change before requesting changes.");
      return;
    }
    const runInfo = message?.metadata?.workflow_run;
    setLoading(true);
    setError("");
    try {
      await respondToApproval(approval.id, { decision, feedback: cleanFeedback });
      setWorkflowApprovalFeedback((current) => {
        const next = { ...current };
        delete next[approval.id];
        return next;
      });
      updateChatMessage(message.id, (current) => ({
        content: decision === "approve"
          ? "Approval received. Casper is finishing the workflow..."
          : decision === "request_changes"
            ? "Change request received. Casper is revising the workflow output..."
          : "Feedback received. Casper is continuing with your decision...",
        status: "streaming",
        metadata: {
          ...(current.metadata || {}),
          workflow_status: {
            ...(current.metadata?.workflow_status || {}),
            progress: {
              ...(current.metadata?.workflow_status?.progress || {}),
              pending_approvals: [],
            },
            pending_approvals: [],
          },
        },
      }));
      if (runInfo) {
        pollWorkflowRun(runInfo, message.id);
      } else {
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      setError(err.message || "Could not submit approval");
    }
  };

  const renderWorkflowRunStatus = (message) => {
    const workflowStatus = message?.metadata?.workflow_status;
    const progress = workflowStatus?.progress;
    const confirmation = message?.metadata?.confirmation_required;
    const workflowPlan = message?.metadata?.workflow_plan;
    const pendingApprovals = progress?.pending_approvals || workflowStatus?.pending_approvals || [];
    const stepOutputs = Array.isArray(workflowStatus?.step_outputs) ? workflowStatus.step_outputs : [];
    const stepTimeline = Array.isArray(workflowStatus?.step_timeline) ? workflowStatus.step_timeline : [];
    const reviewOutput = workflowStatus?.review_output;
    const reviewOutputText = workflowDisplayDataToText(reviewOutput?.text || reviewOutput?.display_data || reviewOutput);
    const visibleStepOutputs = stepOutputs.filter((stepOutput) => (
      isUserFacingWorkflowStepOutput(stepOutput, reviewOutputText)
    ));
    const isPausedForReview = Boolean(pendingApprovals.length) || workflowStatus?.workflow_status === "paused";
    const saveSuggestion = Boolean(
      workflowPlan
      && !message?.metadata?.workflow_saved
      && !message?.metadata?.save_suggestion_dismissed
      && (
        message?.metadata?.save_suggestion
        || workflowStatus?.save_suggestion
        || workflowStatus?.status === "success"
      )
    );
    if (!workflowStatus && !confirmation && !saveSuggestion) return null;

    return (
      <Stack spacing={1.2} sx={{ mt: 1.4 }}>
        {confirmation && (
          <Alert
            severity="warning"
            sx={{
              borderRadius: 2,
              border: "1px solid rgba(184,115,51,0.22)",
              bgcolor: "rgba(255,248,238,0.86)",
            }}
            action={(
              <Button
                size="small"
                variant="contained"
                onClick={() => runWorkflowFromMessage(message, { confirmed: true })}
                disabled={loading}
                sx={{ bgcolor: copper, "&:hover": { bgcolor: copperDark } }}
              >
                Confirm
              </Button>
            )}
          >
            {confirmation.message || "Confirm before Casper starts this workflow."}
          </Alert>
        )}

        {progress && (
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2,
              border: "1px solid rgba(184,115,51,0.18)",
              bgcolor: "rgba(246,240,235,0.50)",
            }}
          >
            <Stack spacing={0.9}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" fontWeight={900} color={copperDark}>
                  {progress.current_step?.label || workflowStatus.workflow_name || "Workflow progress"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {progress.steps_completed || 0}/{progress.steps_total || 0} steps
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Number(progress.percent || 0)}
                sx={{
                  height: 7,
                  borderRadius: 99,
                  bgcolor: "rgba(184,115,51,0.12)",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: copper,
                    borderRadius: 99,
                  },
                }}
              />
              {isPausedForReview && (
                <Alert
                  severity="warning"
                  sx={{
                    borderRadius: 2,
                    border: "1px solid rgba(184,115,51,0.22)",
                    bgcolor: "rgba(255,248,238,0.82)",
                  }}
                >
                  Casper paused for review. Inspect the results below, then approve, request changes, or reject.
                </Alert>
              )}
              {stepTimeline.length > 0 ? (
                <Box>
                  <Typography variant="caption" fontWeight={900} color={copperDark}>
                    Workflow steps
                  </Typography>
                  <Stack spacing={0.75} sx={{ mt: 0.7 }}>
                    {stepTimeline.map((step, index) => {
                      const inputText = workflowStepInputText(step.input);
                      const outputText = workflowTimelineOutputText(step);
                      const approval = step.approval;
                      const approvalPending = approval && String(approval.status || "").toLowerCase() === "pending";
                      return (
                        <Accordion
                          key={step.step_id || `${step.label}-${index}`}
                          disableGutters
                          defaultExpanded={false}
                          elevation={0}
                          sx={{
                            border: `1px solid ${approvalPending ? "rgba(225,126,54,0.42)" : "rgba(184,115,51,0.16)"}`,
                            borderRadius: "10px !important",
                            bgcolor: "rgba(255,255,255,0.74)",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              minHeight: 42,
                              "& .MuiAccordionSummary-content": {
                                alignItems: "center",
                                gap: 1,
                                my: 0.7,
                              },
                            }}
                          >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body2" fontWeight={850}>
                                {step.label || "Workflow step"}
                              </Typography>
                              {step.agent_name && (
                                <Typography variant="caption" color="text.secondary">
                                  {step.agent_name}
                                </Typography>
                              )}
                            </Box>
                            <Chip
                              size="small"
                              label={approvalPending ? "Review" : workflowStepStatusLabel(step.status)}
                              variant="outlined"
                              sx={{
                                height: 22,
                                ...workflowStepStatusSx(approvalPending ? "waiting_for_approval" : step.status),
                                fontWeight: 750,
                              }}
                            />
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <Stack spacing={1}>
                              <Box>
                                <Typography variant="caption" fontWeight={900} color="text.secondary">
                                  Input
                                </Typography>
                                <Box
                                  sx={{
                                    mt: 0.35,
                                    p: 1,
                                    borderRadius: 1.5,
                                    bgcolor: "rgba(246,240,235,0.44)",
                                    border: "1px solid rgba(184,115,51,0.10)",
                                    maxHeight: 160,
                                    overflow: "auto",
                                  }}
                                >
                                  {inputText ? (
                                    <CasperMarkdown compact>{inputText}</CasperMarkdown>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No explicit input captured for this step.
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              <Box>
                                <Typography variant="caption" fontWeight={900} color="text.secondary">
                                  {step.is_approval ? "Review" : "Output"}
                                </Typography>
                                <Box
                                  sx={{
                                    mt: 0.35,
                                    p: 1,
                                    borderRadius: 1.5,
                                    bgcolor: "rgba(255,255,255,0.72)",
                                    border: "1px solid rgba(184,115,51,0.10)",
                                    maxHeight: 260,
                                    overflow: "auto",
                                  }}
                                >
                                  {outputText ? (
                                    <CasperMarkdown compact>{outputText}</CasperMarkdown>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      {String(step.status || "").toLowerCase() === "pending"
                                        ? "Waiting for this step to run."
                                        : "No output captured yet."}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              {approvalPending && (
                                <Box
                                  sx={{
                                    p: 1,
                                    borderRadius: 2,
                                    border: "1px solid rgba(225,126,54,0.28)",
                                    bgcolor: "rgba(255,248,238,0.82)",
                                  }}
                                >
                                  <Typography variant="body2" fontWeight={900}>
                                    Approval required
                                  </Typography>
                                  {approval.prompt && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                                      {approval.prompt}
                                    </Typography>
                                  )}
                                  <TextField
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    placeholder="If you need changes, tell Casper exactly what to revise before clicking Request changes."
                                    value={workflowApprovalFeedback[approval.id] || ""}
                                    onChange={(event) => {
                                      const value = event.target.value;
                                      setWorkflowApprovalFeedback((current) => ({
                                        ...current,
                                        [approval.id]: value,
                                      }));
                                    }}
                                    sx={{
                                      mt: 1,
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        bgcolor: "rgba(255,255,255,0.72)",
                                        "& fieldset": { borderColor: "rgba(184,115,51,0.22)" },
                                        "&:hover fieldset": { borderColor: "rgba(184,115,51,0.38)" },
                                        "&.Mui-focused fieldset": { borderColor: copper },
                                      },
                                    }}
                                  />
                                  <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => respondToWorkflowApproval(approval, message, "approve")}
                                      disabled={loading}
                                      sx={{ bgcolor: copper, "&:hover": { bgcolor: copperDark } }}
                                    >
                                      Approve
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => respondToWorkflowApproval(
                                        approval,
                                        message,
                                        "request_changes",
                                        workflowApprovalFeedback[approval.id],
                                      )}
                                      disabled={loading}
                                      sx={{ borderColor: "rgba(184,115,51,0.34)", color: copperDark }}
                                    >
                                      Request changes
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => respondToWorkflowApproval(approval, message, "reject")}
                                      disabled={loading}
                                      sx={{ color: "text.secondary" }}
                                    >
                                      Reject
                                    </Button>
                                  </Stack>
                                </Box>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Stack>
                </Box>
              ) : visibleStepOutputs.length > 0 && (
                <Box>
                  <Typography variant="caption" fontWeight={900} color={copperDark}>
                    Workflow outputs
                  </Typography>
                  <Stack spacing={0.7} sx={{ mt: 0.7 }}>
                    {visibleStepOutputs.slice(-5).map((stepOutput, index) => {
                      const text = workflowStepOutputText(stepOutput);
                      if (!text) return null;
                      return (
                        <Accordion
                          key={stepOutput.step_id || `${stepOutput.label}-${index}`}
                          disableGutters
                          defaultExpanded={false}
                          elevation={0}
                          sx={{
                            border: "1px solid rgba(184,115,51,0.16)",
                            borderRadius: "8px !important",
                            bgcolor: "rgba(255,255,255,0.74)",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2" fontWeight={850}>
                              {stepOutput.label || "Workflow result"}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <Box sx={{ maxHeight: 280, overflow: "auto" }}>
                              <CasperMarkdown compact>{text}</CasperMarkdown>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Box>
        )}

        {saveSuggestion && (
          <Alert
            severity="success"
            sx={{
              borderRadius: 2,
              border: "1px solid rgba(67,120,72,0.24)",
              bgcolor: "rgba(238,248,239,0.82)",
            }}
            action={(
              <Stack direction="row" spacing={0.75}>
                <Tooltip title="Save workflow design">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => saveWorkflowFromMessage(message)}
                      disabled={loading}
                      sx={{
                        border: "1px solid rgba(67,120,72,0.34)",
                        color: "#437848",
                        bgcolor: "rgba(255,255,255,0.72)",
                        "&:hover": { bgcolor: "rgba(67,120,72,0.10)" },
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Do not save">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => dismissWorkflowSaveSuggestion(message)}
                      disabled={loading}
                      sx={{
                        border: "1px solid rgba(126,116,105,0.22)",
                        color: "text.secondary",
                        bgcolor: "rgba(255,255,255,0.72)",
                        "&:hover": { bgcolor: "rgba(126,116,105,0.08)" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )}
          >
            Save this workflow design for reuse. This saves the underlying workflow, not this execution transcript.
          </Alert>
        )}
      </Stack>
    );
  };

  const renderWorkflowPreview = (message) => {
    const workflowPlan = message?.metadata?.workflow_plan;
    if (!workflowPlan) return null;
    if (
      message?.metadata?.execution_preview
      || message?.status === "preview"
      || message?.metadata?.workflow_run
      || message?.metadata?.workflow_status
      || message?.metadata?.confirmation_required
    ) {
      return null;
    }
    const steps = Array.isArray(workflowPlan.steps) ? workflowPlan.steps : [];
    const validation = message?.metadata?.validation;
    const ready = validation?.valid !== false;
    const reviewState = workflowPlan.review_state || {};
    const approvalRequired = reviewState.requires_approval !== false;
    const isPlaceholderId = (id) => !id || String(id).toUpperCase().includes("PLACEHOLDER");
    const hasUnassignedAgentStep = (validation?.placeholder_steps?.length > 0)
      || steps.some((step) => step.type !== "human_input" && isPlaceholderId(step.agent_id));
    const validationErrors = Array.isArray(validation?.errors) ? validation.errors : [];
    const firstValidationError = validationErrors.length ? validationErrors[0] : null;
    return (
      <Box
        sx={{
          mt: 1.5,
          p: 1.4,
          border: "1px solid rgba(184,115,51,0.18)",
          borderRadius: 2,
          bgcolor: "rgba(246,240,235,0.48)",
        }}
      >
        <Stack spacing={1.2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant="subtitle2" fontWeight={900}>
                {workflowPlan.name || "Casper workflow"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {steps.length} coordinated step{steps.length === 1 ? "" : "s"}
                {ready ? "" : (hasUnassignedAgentStep ? " · needs an assigned specialist" : " · has a validation issue")}
              </Typography>
            </Box>
            {approvalRequired && (
              <Chip
                size="small"
                label="Approval required"
                variant="outlined"
                sx={{
                  borderColor: "rgba(184,115,51,0.42)",
                  color: copperDark,
                  bgcolor: "rgba(184,115,51,0.08)",
                  fontWeight: 800,
                }}
              />
            )}
            <Stack direction="row" spacing={0.75}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => saveWorkflowFromMessage(message)}
                disabled={loading || !ready}
                sx={{ borderColor: "rgba(184,115,51,0.32)", color: copperDark }}
              >
                Save
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => runWorkflowFromMessage(message)}
                disabled={loading || !ready}
                sx={{ bgcolor: copper, "&:hover": { bgcolor: copperDark } }}
              >
                Run
              </Button>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
            {steps.map((step, index) => (
              <Chip
                key={step.id || `${step.user_label}-${index}`}
                size="small"
                label={step.user_label || step.agent_name || step.id}
                variant="outlined"
                sx={{
                  borderColor: step.type === "human_input" || step.agent_id ? "rgba(184,115,51,0.34)" : "rgba(148,163,184,0.5)",
                  bgcolor: step.type === "human_input" ? "rgba(184,115,51,0.10)" : step.agent_id ? "rgba(255,255,255,0.72)" : "rgba(148,163,184,0.10)",
                  color: step.type === "human_input" ? copperDark : "inherit",
                  fontWeight: 700,
                }}
              />
            ))}
          </Stack>
          {approvalRequired && ready && (
            <Typography variant="caption" color="text.secondary">
              This workflow runs as a draft and pauses for human approval before Casper assembles the final deliverable.
            </Typography>
          )}
          {!ready && (
            <Typography variant="caption" color="text.secondary">
              {hasUnassignedAgentStep
                ? "Casper needs a matching agent for every step before this can be saved or run."
                : `This workflow has a validation issue${firstValidationError ? `: ${firstValidationError}` : "."}`}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  };

  const suggestions = profile?.suggestions || [];
  const quickPrompts = suggestions.length ? suggestions.slice(0, 2).concat(QUICK_PROMPTS).slice(0, 4) : QUICK_PROMPTS;
  const hasTranscriptContent = chatMessages.length > 0 || showRoutePicker;
  const workflowHistoryItem = useMemo(() => {
    const localTranscript = readLocalWorkflowTranscript();
    const messages = chatMessages.some(workflowMessageContext)
      ? chatMessages
      : localTranscript?.messages || [];
    const workflowMessage = [...messages].reverse().find((message) => message?.metadata?.workflow_run);
    if (!workflowMessage) return null;
    const userMessage = [...messages].reverse().find((message) => message?.role === "user");
    const workflowStatus = workflowMessage.metadata?.workflow_status;
    const workflowPlan = workflowMessage.metadata?.workflow_plan;
    return {
      id: [
        "workflow",
        workflowMessage.metadata.workflow_run.workflow_id,
        workflowMessage.metadata.workflow_run.run_number,
      ].filter(Boolean).join(":"),
      title: workflowPlan?.name || workflowStatus?.workflow_name || userMessage?.content || "Casper workflow run",
      subtitle: workflowStatus?.status === "success"
        ? "completed workflow"
        : workflowStatus?.workflow_status === "paused"
          ? "paused for review"
          : "workflow run",
      message_count: messages.length,
      updated_at: localTranscript?.updated_at,
      messages,
      workflow_message_id: workflowMessage.id,
      workflow_run: workflowMessage.metadata.workflow_run,
      workflow_status: workflowStatus,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessages, readLocalWorkflowTranscript]);
  const historyItems = useMemo(
    () => buildCasperHistoryItems({ sessions, workflowHistoryItem }),
    [sessions, workflowHistoryItem],
  );
  const totalHistoryCount = historyItems.length;
  const openWorkflowHistoryItem = (item) => {
    if (!item?.messages?.length) return;
    stopActiveStream();
    setSelectedSession(null);
    setSelectedAgent(null);
    setResolution(null);
    setRoutePickerOpen(false);
    setExecutionNotice(null);
    setChatMessages(item.messages);
    setSessionsOpen(false);
    if (item.workflow_message_id && item.workflow_run && !isWorkflowTerminalStatus(item.workflow_status) && item.workflow_status?.workflow_status !== "paused") {
      setLoading(true);
      pollWorkflowRun(item.workflow_run, item.workflow_message_id);
    }
  };
  const openHistoryItem = (item) => {
    if (!item) return;
    if (item.kind === "workflow" && item.messages?.length) {
      openWorkflowHistoryItem(item);
      return;
    }
    if (item.session) {
      openSession(item.session);
      return;
    }
    if (item.sessionId) {
      openSession({ id: item.sessionId });
    }
  };

  return (
    <Box
      sx={{
        maxWidth: "min(1760px, calc(100vw - 48px))",
        width: "100%",
        mx: "auto",
        px: { xs: 1.5, md: 2 },
        py: { xs: 1.5, md: 2.5 },
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {showIntro && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(251, 250, 248, 0.86)",
            backdropFilter: "blur(12px)",
            animation: "casperIntroFade 1.7s ease forwards",
            "@keyframes casperIntroFade": {
              "0%": { opacity: 0 },
              "18%": { opacity: 1 },
              "78%": { opacity: 1 },
              "100%": { opacity: 0, pointerEvents: "none" },
            },
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CasperGhost size={96} />
            <Typography variant="h4" fontWeight={900}>
              Casper is ready.
            </Typography>
          </Stack>
        </Box>
      )}

      <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ flexShrink: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.6}>
            <CasperGhost size={50} />
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h3" fontWeight={900} sx={{ letterSpacing: 0 }}>
                  Casper
                </Typography>
                <Chip label="Live" color="success" variant="soft" size="small" sx={{ fontWeight: 800 }} />
                <Chip
                  label="BETA"
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: isDarkMode ? "rgba(224,177,132,0.44)" : "rgba(184,115,51,0.42)",
                    color: casperCopperText,
                    bgcolor: isDarkMode ? "rgba(224,177,132,0.12)" : "rgba(184,115,51,0.08)",
                    fontWeight: 900,
                    letterSpacing: 0.4,
                  }}
                />
              </Stack>
              <Typography variant="h6" color="text.secondary" sx={{ mt: 0.25 }}>
                Ask, upload, create, or search in one place.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            {setupAlertVisible && (
              <Tooltip
                title={setupBlocking
                  ? `${setupAlertCount} setup task${setupAlertCount === 1 ? "" : "s"} required before launch`
                  : `${setupAlertCount} optional setup task${setupAlertCount === 1 ? "" : "s"} pending`}
              >
                <IconButton
                  aria-label="Open Casper setup assistant"
                  onClick={() => setSetupDrawerOpen(true)}
                  sx={{
                    width: 46,
                    height: 46,
                    border: `1px solid ${isDarkMode ? "rgba(224,177,132,0.28)" : "rgba(184,115,51,0.26)"}`,
                    bgcolor: setupBlocking
                      ? (isDarkMode ? "rgba(255,184,89,0.16)" : "rgba(255,152,0,0.13)")
                      : (isDarkMode ? "rgba(224,177,132,0.12)" : "rgba(184,115,51,0.08)"),
                    color: setupBlocking ? (isDarkMode ? "#FFD09A" : "#C46A00") : casperCopperText,
                    boxShadow: "0 12px 26px rgba(20,18,15,0.08)",
                    animation: setupBlocking ? "casperSetupPulse 1.8s ease-in-out infinite" : "none",
                    "@keyframes casperSetupPulse": {
                      "0%, 100%": { transform: "translateY(0)", boxShadow: "0 12px 26px rgba(20,18,15,0.08)" },
                      "50%": { transform: "translateY(-2px)", boxShadow: "0 16px 34px rgba(184,115,51,0.22)" },
                    },
                    "&:hover": {
                      bgcolor: isDarkMode ? "rgba(224,177,132,0.18)" : "rgba(184,115,51,0.14)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Badge
                    badgeContent={setupAlertCount}
                    color={setupBlocking ? "warning" : "primary"}
                    sx={{ "& .MuiBadge-badge": { fontWeight: 900 } }}
                  >
                    <WarningAmberIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <Paper
          component="form"
          onSubmit={handleSubmit}
          onDragEnter={handleChatDragEnter}
          onDragOver={handleChatDragOver}
          onDragLeave={handleChatDragLeave}
          onDrop={handleChatDrop}
          elevation={0}
          sx={{
            position: "relative",
            flex: "1 1 auto",
            minHeight: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            p: { xs: 1.5, md: 2 },
            border: "none",
            borderRadius: 0,
            background: "transparent",
            backdropFilter: "none",
            boxShadow: "none",
          }}
        >
          {isDraggingFiles && (
            <Box
              sx={{
                position: "absolute",
                inset: 12,
                zIndex: 4,
                border: `2px dashed ${isDarkMode ? "rgba(224,177,132,0.62)" : "rgba(184,115,51,0.58)"}`,
                borderRadius: 2,
                bgcolor: isDarkMode ? "rgba(18,14,11,0.88)" : "rgba(255,252,249,0.86)",
                backdropFilter: "blur(6px)",
                display: "grid",
                placeItems: "center",
                pointerEvents: "none",
                boxShadow: isDarkMode
                  ? "inset 0 0 0 1px rgba(224,177,132,0.18)"
                  : "inset 0 0 0 1px rgba(255,255,255,0.65)",
              }}
            >
              <Stack alignItems="center" spacing={1}>
                <UploadFileIcon sx={{ color: casperCopperText, fontSize: 34 }} />
                <Typography fontWeight={900} color={casperCopperText}>
                  Drop files to add them to this chat
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Casper will index them for this session automatically.
                </Typography>
              </Stack>
            </Box>
          )}
          <Stack spacing={1.25} sx={{ flex: "1 1 auto", minHeight: 0, height: "100%" }}>
            <Box
              sx={{
                flex: "1 1 auto",
                px: { xs: 1, md: 2 },
                py: { xs: 1.25, md: 2 },
                minHeight: 0,
                overflowY: "auto",
                borderRadius: 0,
                border: "none",
                bgcolor: "transparent",
                overscrollBehavior: "contain",
              }}
            >
              {hasTranscriptContent ? (
                <Stack spacing={1.75}>
                  {chatMessages.map((message) => {
                    const isUserMessage = message.role === "user";
                    const isStatus = message.kind === "status";
                    const isOnboardingGuide = message.kind === "onboarding"
                      || message.metadata?.kind === "onboarding_guide";
                    const isWorking = ["pending", "streaming"].includes(message.status) && !message.content;
                    const showExecutionPreviewCard = shouldShowExecutionPreviewCard({ message });
                    const hasInteractionDecision = Boolean(message.metadata?.interaction_decision);
                    const showInteractionDecisionCard = hasInteractionDecision && shouldShowInteractionDecisionCard({
                      decision: message.metadata?.interaction_decision,
                      hasExecutionPreview: showExecutionPreviewCard,
                    });
                    if (isOnboardingGuide) {
                      return null;
                    }
                    if (isStatus) {
                      return (
                        <Stack key={message.id} direction="row" alignItems="center" spacing={1} sx={{ color: copperDark }}>
                          <CasperGhost size={24} />
                          <Typography variant="body2" fontWeight={800}>
                            {message.content}
                          </Typography>
                        </Stack>
                      );
                    }
                    return (
                      <Box
                        key={message.id}
                        sx={{
                          display: "flex",
                          justifyContent: isUserMessage ? "flex-end" : "flex-start",
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: isUserMessage ? { xs: "94%", md: "48%" } : "100%",
                            width: isUserMessage ? "fit-content" : "100%",
                          }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              width: "100%",
                              px: { xs: 2, md: 2.5 },
                              py: { xs: 1.45, md: 1.9 },
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: casperBorder,
                              bgcolor: isUserMessage ? casperUserSurface : casperSurface,
                              color: "text.primary",
                              boxShadow: isUserMessage
                                ? "none"
                                : isDarkMode
                                  ? "0 14px 34px rgba(0,0,0,0.26)"
                                  : "0 14px 34px rgba(20,18,15,0.06)",
                            }}
                          >
                            {isUserMessage && (
                              <PromptAttachmentPreview
                                attachments={message.attachments || message.metadata?.attachments || []}
                              />
                            )}
                            {isWorking ? (
                              <Stack direction="row" alignItems="center" spacing={1.2}>
                                <WorkingCasperIcon />
                                <Typography color="text.secondary">Working on it...</Typography>
                              </Stack>
                            ) : isOnboardingGuide ? (
                              <CasperOnboardingGuideMessage
                                status={onboardingStatus}
                                loading={onboardingLoading || loading}
                                skippedTaskIds={onboardingSkippedTasks}
                                onSaveCredential={saveOnboardingCredential}
                                onSkipTask={skipOnboardingTask}
                                onResetSkipped={resetSkippedOnboardingTasks}
                                onRefresh={refreshOnboarding}
                              />
                            ) : showExecutionPreviewCard ? null : (
                              <CasperMarkdown compact={isUserMessage}>{message.content}</CasperMarkdown>
                            )}
                            {!isUserMessage && !isOnboardingGuide && (
                              <>
                                {showExecutionPreviewCard && (
                                  showInteractionDecisionCard ? (
                                    <CasperInteractionDecisionCard
                                      decision={message.metadata.interaction_decision}
                                      onUseResource={(entity) => {
                                        setIntent(`Use ${entity.display_name || entity.name || entity.id} for this task`);
                                        setSelectedAgent(entity.type === "agent" ? entity : selectedAgent);
                                      }}
                                    />
                                  ) : (
                                  message.metadata.execution_preview?.run_card ? (
                                    <CasperRunCard
                                      runCard={message.metadata.execution_preview.run_card}
                                      confidence={message.metadata.execution_preview.confidence}
                                      previewToken={message.metadata.execution_preview.confirmation_token || message.metadata.execution_preview.preview_token}
                                      loading={loading}
                                      onRun={(inputs) => runExecutionPreviewMessage({ ...message, metadata: { ...message.metadata, run_card_inputs: inputs } })}
                                      onPickAlternative={(resource) => changeExecutionPreviewRoute(message, resource)}
                                      onBuildFresh={() => changeExecutionPreviewRoute(message, { type: "build_fresh" })}
                                      onBuildWorkflow={() => changeExecutionPreviewRoute(message, { type: "build_fresh" })}
                                    />
                                  ) : (
                                  <CasperExecutionPreviewCard
                                    preview={message.metadata.execution_preview}
                                    loading={loading}
                                    onRun={() => runExecutionPreviewMessage(message)}
                                    onChange={() => changeExecutionPreviewRoute(message)}
                                    onDirectAnswer={() => runExecutionPreviewDirectAnswer(message)}
                                    onOpenSetup={() => setSetupDrawerOpen(true)}
                                    onCopyAdminNote={(note) => copyTextToClipboard(note)}
                                  />
                                  )
                                  )
                                )}
                                {renderWorkflowPreview(message)}
                                {renderWorkflowRunStatus(message)}
                                <MessageArtifactGallery
                                  message={message}
                                  sessionData={selectedSession}
                                  onDownload={downloadArtifact}
                                />
                              </>
                            )}
                          </Paper>
                          {isUserMessage ? renderPromptActions(message) : renderResponseActions(message)}
                        </Box>
                      </Box>
                    );
                  })}

                  {showRoutePicker && (
                    <Box
                      sx={{
                        mt: 0.5,
                        p: { xs: 1.25, md: 1.5 },
                        border: `1px solid ${isDarkMode ? "rgba(224,177,132,0.22)" : "rgba(184,115,51,0.18)"}`,
                        borderRadius: 2,
                        bgcolor: isDarkMode ? "rgba(33,27,22,0.72)" : "rgba(255,255,255,0.66)",
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight={900}>
                          {routePickerTitle()}
                        </Typography>
                        <Tooltip title="Clear">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setResolution(null);
                              setRoutePickerOpen(false);
                              setSelectedAgent(null);
                              setResolvedIntent("");
                              setExecutionNotice(null);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Grid container spacing={1.5}>
                        {routePickerEntities.map((entity) => (
                          <Grid item xs={12} md={6} key={`${entity.type}-${entity.id}-${entity.version || ""}`}>
                            <EntityCard
                              entity={entity}
                              selected={selectedAgent?.id === entity.id && entity.type === "agent"}
                              onSelect={() => {
                                if (entity.type === "agent") {
                                  setSelectedAgent(entity);
                                  setExecutionNotice(null);
                                }
                              }}
                              onRun={() => executeEntity(entity)}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                  <Box ref={chatEndRef} />
                </Stack>
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{
                    height: "100%",
                    minHeight: 180,
                    textAlign: "center",
                    color: "text.secondary",
                  }}
                >
                  <CasperGhost size={34} />
                  <Typography fontWeight={800}>Ask Casper or drop files here.</Typography>
                </Stack>
              )}
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                pt: chatMessages.length || showRoutePicker ? 0.75 : 0,
                borderRadius: 2,
                bgcolor: isDarkMode ? "rgba(18,14,11,0.64)" : "rgba(255,255,255,0.50)",
              }}
            >
              <PromptAttachmentPreview
                attachments={pendingPromptFiles}
                onRemove={removePendingPromptFile}
              />
              <TextField
                inputRef={inputRef}
                value={intent}
                onChange={(event) => setIntent(event.target.value)}
                multiline
                minRows={3}
                placeholder="What do you want to do?"
                fullWidth
                disabled={loading}
                InputProps={{
                  sx: {
                    fontSize: "1.18rem",
                    borderRadius: 2,
                    bgcolor: casperSoftSurface,
                    color: "text.primary",
                    "& fieldset": { borderColor: isDarkMode ? "rgba(224,177,132,0.16)" : "transparent" },
                    "&:hover fieldset": { borderColor: isDarkMode ? "rgba(224,177,132,0.34)" : "rgba(184,115,51,0.22)" },
                    "&.Mui-focused fieldset": { borderColor: isDarkMode ? "#D1935C" : copper },
                  },
                }}
              />
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "center" }}
                spacing={1.5}
                sx={{ mt: 1.35 }}
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {selectedAgent && (
                    <Chip
                      label={selectedAgent.display_name || selectedAgent.name}
                      onDelete={() => {
                        setSelectedAgent(null);
                        setExecutionNotice(null);
                      }}
                      color="primary"
                      variant="soft"
                    />
                  )}
                  {!selectedAgent && quickPrompts.map((suggestion) => (
                    <Chip
                      key={suggestion}
                      label={suggestion}
                      onClick={() => {
                        setIntent(suggestion);
                      }}
                      variant="outlined"
                      sx={{
                        borderColor: isDarkMode ? "rgba(224,177,132,0.32)" : "rgba(184,115,51,0.34)",
                        bgcolor: isDarkMode ? "rgba(33,27,22,0.70)" : "rgba(255,255,255,0.7)",
                        color: "text.primary",
                      }}
                    />
                  ))}
                </Stack>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={handleUploadFiles}
                  />
                  <Tooltip title="Upload files">
                    <span>
                      <IconButton
                        type="button"
                        aria-label="Upload files"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={loading || uploading}
                        sx={commandIconSx}
                      >
                        {uploading ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Sessions">
                    <IconButton
                      type="button"
                      aria-label="Open sessions"
                      onClick={() => {
                        refreshSessions();
                        setSessionsOpen(true);
                      }}
                      sx={commandIconSx}
                    >
                      <Badge
                        badgeContent={totalHistoryCount || sessionRunCount || sessions.length || 0}
                        color="primary"
                        max={99}
                        invisible={(totalHistoryCount || sessionRunCount || sessions.length || 0) === 0}
                        overlap="circular"
                      >
                        <HistoryIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Artifacts">
                    <IconButton
                      type="button"
                      aria-label="Open artifacts"
                      onClick={openArtifacts}
                      sx={commandIconSx}
                    >
                      <Badge
                        badgeContent={artifacts.length}
                        color="primary"
                        max={99}
                        invisible={!artifacts.length}
                        overlap="circular"
                      >
                        <Inventory2OutlinedIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={loading ? "Casper is working" : "Ask Casper"}>
                    <span>
                      <IconButton
                        type="submit"
                        aria-label={loading ? "Casper is working" : "Ask Casper"}
                        disabled={!loading && !intent.trim()}
                        sx={{
                          ...commandIconSx,
                          color: "#fff",
                          bgcolor: copper,
                          "&:hover": {
                            bgcolor: copperDark,
                            transform: "translateY(-1px)",
                            boxShadow: "0 16px 32px rgba(184,115,51,0.24)",
                          },
                        }}
                      >
                        {loading ? <WorkingCasperIcon /> : <SendIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>

            {uploadNotice && (
              <Alert severity={uploadNotice.toLowerCase().includes("failed") ? "error" : "info"} onClose={() => setUploadNotice("")}>
                {uploadNotice}
              </Alert>
            )}
            {executionNotice?.status === "auth_required" && (
              <Alert severity={executionNotice.status === "auth_required" ? "warning" : "info"} onClose={() => setExecutionNotice(null)}>
                {executionNotice.message || "Casper action prepared."}
              </Alert>
            )}
            {authRequired.length > 0 && (
              <Stack spacing={1}>
                {authRequired.map((signal) => (
                  <Alert
                    key={`${signal.provider}-${signal.required_env || signal.tool_name || "oauth"}`}
                    severity="warning"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        startIcon={<VpnKeyIcon />}
                        onClick={() => connectOAuth(signal)}
                      >
                        Connect
                      </Button>
                    }
                  >
                    {signal.message || `Connect ${signal.provider_label || signal.provider} to continue.`}
                  </Alert>
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>
      </Stack>

      <Drawer
        anchor="right"
        open={setupDrawerOpen}
        onClose={() => setSetupDrawerOpen(false)}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 6 }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 470, md: 560 },
            maxWidth: "100vw",
            p: 0,
            borderLeft: `1px solid ${isDarkMode ? "rgba(224,177,132,0.24)" : "rgba(184,115,51,0.20)"}`,
            bgcolor: isDarkMode ? "#181411" : "#fffdfa",
            color: "text.primary",
            boxShadow: isDarkMode ? "-18px 0 48px rgba(0,0,0,0.44)" : "-18px 0 48px rgba(20,18,15,0.16)",
          },
        }}
      >
        <Stack sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.35}
            sx={{
              px: 2,
              py: 1.7,
              borderBottom: `1px solid ${isDarkMode ? "rgba(224,177,132,0.18)" : "rgba(184,115,51,0.14)"}`,
              bgcolor: isDarkMode ? "rgba(24,20,17,0.94)" : "rgba(255,252,249,0.92)",
              backdropFilter: "blur(12px)",
            }}
          >
            <CasperGhost size={38} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Typography variant="h6" fontWeight={950} noWrap>
                  Casper Setup
                </Typography>
                <Chip
                  size="small"
                  label={`${onboardingStatus?.completion_percent || 0}% ready`}
                  sx={{
                    height: 23,
                    bgcolor: setupBlocking
                      ? (isDarkMode ? "rgba(255,184,89,0.18)" : "rgba(255,152,0,0.12)")
                      : (isDarkMode ? "rgba(91,160,92,0.16)" : "rgba(67,120,72,0.10)"),
                    color: setupBlocking ? (isDarkMode ? "#FFE2BD" : "#9A5A00") : (isDarkMode ? "#DDF6DD" : "#437848"),
                    fontWeight: 900,
                  }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" noWrap>
                Secure setup, one service at a time.
              </Typography>
            </Box>
            <Tooltip title="Refresh setup">
              <span>
                <IconButton onClick={refreshOnboarding} disabled={onboardingLoading} sx={{ color: casperCopperText }}>
                  {onboardingLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={() => setSetupDrawerOpen(false)} sx={{ color: "text.secondary" }}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              px: { xs: 1.5, sm: 2 },
              py: 2,
            }}
          >
            <Stack spacing={1.5}>
              <Paper
                elevation={0}
                sx={{
                  alignSelf: "flex-start",
                  maxWidth: "92%",
                  p: 1.6,
                  borderRadius: "20px 20px 20px 6px",
                  border: `1px solid ${isDarkMode ? "rgba(224,177,132,0.18)" : "rgba(184,115,51,0.16)"}`,
                  bgcolor: casperSurface,
                  color: "text.primary",
                  boxShadow: isDarkMode ? "0 12px 28px rgba(0,0,0,0.24)" : "0 12px 28px rgba(20,18,15,0.06)",
                }}
              >
                <Stack direction="row" spacing={1.1} alignItems="flex-start">
                  <CasperGhost size={28} />
                  <Box>
                    <Typography fontWeight={900}>
                      I checked this workspace for you.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                      {setupBlocking
                        ? "Some required services still need credentials before Casper can run dependent agents and tools."
                        : setupAlertVisible
                          ? "Core Casper is usable. These optional services can be connected now or later."
                          : "Everything required is ready. I’ll keep watching for future setup tasks."}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <CasperOnboardingGuideMessage
                status={onboardingStatus}
                loading={onboardingLoading || loading}
                skippedTaskIds={onboardingSkippedTasks}
                onSaveCredential={saveOnboardingCredential}
                onSkipTask={skipOnboardingTask}
                onResetSkipped={resetSkippedOnboardingTasks}
                onRefresh={refreshOnboarding}
              />
            </Stack>
          </Box>
        </Stack>
      </Drawer>

      <Drawer
        anchor="left"
        open={artifactsOpen}
        onClose={() => setArtifactsOpen(false)}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 4 }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 380 },
            p: 0,
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        <Stack sx={{ height: "100%" }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 2.25 }}>
            <Inventory2OutlinedIcon sx={{ color: copper }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={900}>Artifacts</Typography>
              <Typography variant="body2" color="text.secondary">Files created in this session</Typography>
            </Box>
            <IconButton onClick={() => setArtifactsOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          <Stack spacing={1.25} sx={{ p: 2, overflow: "auto", flex: 1 }}>
            {artifactsLoading && (
              <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
                <CircularProgress size={26} sx={{ color: copper }} />
                <Typography color="text.secondary">Loading artifacts</Typography>
              </Stack>
            )}
            {!artifactsLoading && artifactError && (
              <Alert severity="error" onClose={() => setArtifactError("")}>
                {artifactError}
              </Alert>
            )}
            {!artifactsLoading && !artifactError && artifacts.length === 0 && (
              <Stack alignItems="center" spacing={1.5} sx={{ py: 7, textAlign: "center" }}>
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "rgba(184,115,51,0.10)",
                    color: copper,
                  }}
                >
                  <Inventory2OutlinedIcon />
                </Box>
                <Box>
                  <Typography fontWeight={850}>No artifacts yet</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Generated files will appear here. Workflow review output appears inline in the chat.
                  </Typography>
                </Box>
              </Stack>
            )}
            {!artifactsLoading && artifacts.map((artifact) => (
              <Paper
                key={artifact.id || artifact.filename}
                elevation={0}
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.78)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(184,115,51,0.10)",
                      color: copper,
                    }}
                  >
                    <Inventory2OutlinedIcon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={800} noWrap>
                      {artifact.filename || artifact.name || "Artifact"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ready to download
                    </Typography>
                  </Box>
                  <Tooltip title="Download">
                    <IconButton
                      aria-label={`Download ${artifact.filename || "artifact"}`}
                      onClick={() => downloadArtifact(artifact)}
                      sx={{ color: copperDark }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Stack>
      </Drawer>

      <Drawer
        anchor="left"
        open={sessionsOpen}
        onClose={() => setSessionsOpen(false)}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 4 }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 380 },
            p: 0,
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        <Stack sx={{ height: "100%" }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 2.25 }}>
            <HistoryIcon sx={{ color: copper }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={900}>Previous Runs</Typography>
              <Typography variant="body2" color="text.secondary">
                {totalHistoryCount} run{totalHistoryCount === 1 ? "" : "s"}
                {sessionMessageCount ? ` · ${sessionMessageCount} messages` : ""}
              </Typography>
            </Box>
            <IconButton onClick={() => setSessionsOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          <Stack spacing={1} sx={{ p: 2, overflow: "auto", flex: 1 }}>
            {totalHistoryCount === 0 && (
              <Typography color="text.secondary">No Casper history yet.</Typography>
            )}
            {historyItems.map((item) => (
              <Paper
                key={item.id}
                elevation={0}
                onClick={() => openHistoryItem(item)}
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: item.kind === "workflow" ? "rgba(184,115,51,0.34)" : "divider",
                  borderRadius: 2,
                  cursor: "pointer",
                  bgcolor: item.kind === "workflow" ? "rgba(246,240,235,0.58)" : "background.paper",
                  "&:hover": {
                    borderColor: copper,
                    boxShadow: "0 10px 26px rgba(20,18,15,0.08)",
                  },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={850} noWrap>{item.title || "Casper run"}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.subtitle || (item.kind === "session" ? "Saved thread" : "Casper run")}
                      {item.updated_at ? ` · ${formatSessionTime(item.updated_at)}` : ""}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={item.kind === "run"
                      ? "run"
                      : `${item.message_count || 0} msg${Number(item.message_count || 0) === 1 ? "" : "s"}`}
                    variant="outlined"
                    sx={{ borderColor: "rgba(184,115,51,0.35)", color: copperDark, fontWeight: 800 }}
                  />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Stack>
      </Drawer>
    </Box>
  );
}
